import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, InputNumber, Select,
  Space, Tag, Tabs, message, Popconfirm, Row, Col, Statistic,
  Alert, Progress, Tooltip, Switch,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, UploadOutlined, DownloadOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { saveAs } from 'file-saver';
import './index.less';

// ==================== OSS 图片缩放辅助函数 ====================
// 为 OSS 图片 URL 添加缩放参数，加快列表加载速度
// 非 OSS 地址（不含 aliyuncs.com）原样返回
const ossThumb = (url: string | undefined, w: number): string => {
  if (!url) return '';
  const param = `x-oss-process=image/resize,w_${w}`;
  if (!url.includes('aliyuncs.com')) return url;
  return url.includes('?') ? `${url}&${param}` : `${url}?${param}`;
};

// ==================== 通用头像上传组件 ====================
const AvatarUpload: React.FC<{
  uploadUrl: string;
  nameField: string;
  value?: string;
  onChange?: (url: string) => void;
}> = ({ uploadUrl, nameField, value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', nameField);
      const res = await fetch(uploadUrl, { method: 'POST', body: formData });
      const json = await res.json();
      if (json.success) { onChange?.(json.data.url); message.success('上传成功'); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
    finally { setUploading(false); e.target.value = ''; }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space.Compact style={{ width: '100%' }}>
        <Input value={value} onChange={e => onChange?.(e.target.value)} placeholder="https://..." />
        <Button icon={<UploadOutlined />} loading={uploading} onClick={() => inputRef.current?.click()}>上传</Button>
      </Space.Compact>
      {value && <img src={ossThumb(value, 200)} alt="preview" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #d9d9d9' }} />}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
    </Space>
  );
};


const { TabPane } = Tabs;
const BASE_URL = '/api/v1/urban-rpg';

const RARITY_COLOR: Record<string, string> = { N: '#8c8c8c', R: '#1677ff', SR: '#722ed1', UR: '#faad14' };
const DIFFICULTY_LABEL = ['', '★', '★★', '★★★', '★★★★', '★★★★★'];

interface Item {
  id: number; name: string; nameEn?: string; type: string; value: number; valuePercent: number; description?: string; iconUrl?: string;
}
interface BossLoot {
  id?: number; itemId: number; dropChance: number; minQuantity: number; maxQuantity: number;
  item?: Item;
}

interface HeroType {
  id: number; code: string; name: string; nameEn?: string; description: string; avatarUrl?: string;
  specialty?: string; baseHp: number; baseAtk: number; baseDef: number; baseSpeed: number;
  attributes: string[]; displayOrder: number; isActive: boolean; skillLevelCount: number;
}
interface Skill {
  id: number; name: string; nameEn?: string; attribute: string; description?: string;
  skillType: string; power: number; statusEffect?: string; statusChance: number; statusDuration: number;
  criticalChance: number; criticalDamage: number;
}
interface BossPhase2 {
  id?: number; bossId: number; hp: number; atk: number; def: number; speed: number;
  attributes?: string[]; avatarUrl?: string; description?: string; skills?: Skill[];
}

interface Boss {
  id: number; code: string; cardCode?: string; name: string; nameEn?: string; description?: string; avatarUrl?: string;
  rarity: string; difficulty: number; hp: number; atk: number; def: number; expReward: number; speed: number;
  phase2Chance: number; hasPhase2: boolean;
  phase2Data?: { hp: number; atk: number; def: number; speed: number; attributes?: string[]; avatarUrl?: string; description?: string; skills?: Skill[] } | null;
  attributes: string[]; cooldownSeconds: number; isActive: boolean;
  skills: Skill[];
}
interface HeroLevel {
  id?: number; level: number; requiredExp: number;
  hpBonus: number; atkBonus: number; defBonus: number; speedBonus: number;
  isEvolution: boolean; evolutionImageUrl?: string;
}
interface GameCard { id: number; cardCode: string; bossId: number; boss: Boss; isActive: boolean; created: string; }
interface HeroInstance {
  id: number; heroTypeId: number; heroType?: { id: number; name: string };
  level: number; exp: number; hp: number; atk: number; def: number;
  battlesWon: number; battlesLost: number; isDead: boolean;
}
interface Player {
  id: number; hash: string; nickname: string; hero: HeroInstance | null; created: string;
}

// ==================== 英雄类型 CSV 导入工具函数 ====================

// ---- 英雄类型 CSV ----
const HERO_CSV_HEADERS = ['code','name','nameEn','attributes','baseHp','baseAtk','baseDef','baseSpeed','specialty','description','displayOrder'];
const HERO_CSV_TEMPLATE_ROW = ['houyi','后羿','Hou Yi','火|金','120','25','10','14','精准射击擅长攻击','射日英雄后羿，神箭无双','1'];

interface HeroImportRow {
  _line: number;
  code: string; name: string; nameEn?: string;
  attributes: string[]; baseHp: number; baseAtk: number; baseDef: number; baseSpeed: number;
  specialty?: string; description?: string; displayOrder: number;
  _error?: string;
}

function parseHeroCSV(text: string): HeroImportRow[] {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter(l => l.trim());
  const dataLines = lines[0].toLowerCase().startsWith('code') ? lines.slice(1) : lines;
  return dataLines.map((line, idx) => {
    const cols = parseCSVLine(line);
    const [code, name, nameEn, attributes, baseHp, baseAtk, baseDef, baseSpeed, specialty, description, displayOrder] = cols;
    const errors: string[] = [];
    if (!code) errors.push('缺少编码');
    if (!name) errors.push('缺少名称');
    return {
      _line: idx + 2,
      code: code?.trim(), name: name?.trim(), nameEn: nameEn?.trim() || undefined,
      attributes: attributes ? attributes.split('|').map(a => a.trim()).filter(Boolean) : [],
      baseHp: Number(baseHp) || 100, baseAtk: Number(baseAtk) || 15,
      baseDef: Number(baseDef) || 8, baseSpeed: Number(baseSpeed) || 10,
      specialty: specialty?.trim() || undefined, description: description?.trim() || undefined,
      displayOrder: Number(displayOrder) || 10,
      _error: errors.length ? errors.join('；') : undefined,
    };
  }).filter(r => r.code || r.name);
}

function downloadHeroTemplate() {
  const csv = [HERO_CSV_HEADERS.join(','), HERO_CSV_TEMPLATE_ROW.join(',')].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'hero_type_template.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ---- 英雄等级 CSV ----
const HERO_LEVEL_CSV_HEADERS = ['heroCode','level','requiredExp','hpBonus','atkBonus','defBonus','speedBonus','isEvolution'];
const HERO_LEVEL_CSV_TEMPLATE_ROW = ['houyi','2','100','15','3','1','1','false'];

interface HeroLevelImportRow {
  _line: number;
  heroCode: string; level: number; requiredExp: number;
  hpBonus: number; atkBonus: number; defBonus: number; speedBonus: number;
  isEvolution: boolean;
  _error?: string;
}

function parseHeroLevelCSV(text: string): HeroLevelImportRow[] {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter(l => l.trim());
  const dataLines = lines[0].toLowerCase().startsWith('herocode') ? lines.slice(1) : lines;
  return dataLines.map((line, idx) => {
    const cols = parseCSVLine(line);
    const [heroCode, level, requiredExp, hpBonus, atkBonus, defBonus, speedBonus, isEvolution] = cols;
    const errors: string[] = [];
    if (!heroCode) errors.push('缺少heroCode');
    const lvl = Number(level);
    if (!lvl || lvl < 2) errors.push('等级须≥2');
    return {
      _line: idx + 2,
      heroCode: heroCode?.trim(), level: lvl || 2, requiredExp: Number(requiredExp) || 100,
      hpBonus: Number(hpBonus) || 0, atkBonus: Number(atkBonus) || 0,
      defBonus: Number(defBonus) || 0, speedBonus: Number(speedBonus) || 0,
      isEvolution: isEvolution?.toLowerCase() === 'true',
      _error: errors.length ? errors.join('；') : undefined,
    };
  }).filter(r => r.heroCode);
}

function downloadHeroLevelTemplate() {
  const rows = ['2','3','4','5','6','7','8','9','10'].map((lv, i) => [
    'houyi', lv, String((i + 1) * 100), '15', '3', '1', '1', 'false'
  ]);
  const csv = [HERO_LEVEL_CSV_HEADERS.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'hero_level_template.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ---- 英雄技能习得 CSV ----
const HERO_SKILL_LEVEL_CSV_HEADERS = ['heroCode','skillName','requiredLevel'];
const HERO_SKILL_LEVEL_CSV_TEMPLATE_ROW = ['houyi','灼日箭','1'];

interface HeroSkillLevelImportRow {
  _line: number;
  heroCode: string; skillName: string; requiredLevel: number;
  _error?: string;
}

function parseHeroSkillLevelCSV(text: string): HeroSkillLevelImportRow[] {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter(l => l.trim());
  const dataLines = lines[0].toLowerCase().startsWith('herocode') ? lines.slice(1) : lines;
  return dataLines.map((line, idx) => {
    const cols = parseCSVLine(line);
    const [heroCode, skillName, requiredLevel] = cols;
    const errors: string[] = [];
    if (!heroCode) errors.push('缺少heroCode');
    if (!skillName) errors.push('缺少skillName');
    return {
      _line: idx + 2,
      heroCode: heroCode?.trim(), skillName: skillName?.trim(),
      requiredLevel: Number(requiredLevel) || 1,
      _error: errors.length ? errors.join('；') : undefined,
    };
  }).filter(r => r.heroCode || r.skillName);
}

function downloadHeroSkillLevelTemplate() {
  const rows = [
    ['houyi', '灼日箭', '1'],
    ['houyi', '太阳之矢', '1'],
    ['houyi', '穿云箭', '5'],
    ['houyi', '射日神箭', '10'],
  ];
  const csv = [HERO_SKILL_LEVEL_CSV_HEADERS.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'hero_skill_level_template.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ==================== 英雄类型 Tab ====================
const HeroTypesTab: React.FC = () => {
  const [data, setData] = useState<HeroType[]>([]);
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<HeroType | null>(null);
  const [skillLevelHero, setSkillLevelHero] = useState<HeroType | null>(null);
  const [skillLevelLoading, setSkillLevelLoading] = useState(false);
  const [levelHero, setLevelHero] = useState<HeroType | null>(null);
  const [levelLoading, setLevelLoading] = useState(false);
  const [form] = Form.useForm();
  const [levelForm] = Form.useForm();
  const [skillLevelForm] = Form.useForm();

  // Hero type bulk import state
  const [heroImportOpen, setHeroImportOpen] = useState(false);
  const [heroImportRows, setHeroImportRows] = useState<HeroImportRow[]>([]);
  const [heroImportLoading, setHeroImportLoading] = useState(false);
  const [heroImportProgress, setHeroImportProgress] = useState(0);
  const [heroImportDone, setHeroImportDone] = useState<{ ok: number; fail: number } | null>(null);
  const heroFileInputRef = useRef<HTMLInputElement>(null);

  // Hero level bulk import state
  const [levelImportOpen, setLevelImportOpen] = useState(false);
  const [levelImportRows, setLevelImportRows] = useState<HeroLevelImportRow[]>([]);
  const [levelImportLoading, setLevelImportLoading] = useState(false);
  const [levelImportProgress, setLevelImportProgress] = useState(0);
  const [levelImportDone, setLevelImportDone] = useState<{ ok: number; fail: number } | null>(null);
  const levelFileInputRef = useRef<HTMLInputElement>(null);

  // Hero skill-level bulk import state
  const [skillLevelImportOpen, setSkillLevelImportOpen] = useState(false);
  const [skillLevelImportRows, setSkillLevelImportRows] = useState<HeroSkillLevelImportRow[]>([]);
  const [skillLevelImportLoading, setSkillLevelImportLoading] = useState(false);
  const [skillLevelImportProgress, setSkillLevelImportProgress] = useState(0);
  const [skillLevelImportDone, setSkillLevelImportDone] = useState<{ ok: number; fail: number } | null>(null);
  const skillLevelFileInputRef = useRef<HTMLInputElement>(null);

  // Hero bulk image upload state
  const [heroFrontUploadOpen, setHeroFrontUploadOpen] = useState(false);
  const [heroFrontItems, setHeroFrontItems] = useState<HeroImgUploadItem[]>([]);
  const [heroFrontUploading, setHeroFrontUploading] = useState(false);
  const heroFrontInputRef = useRef<HTMLInputElement>(null);

  const [heroBackUploadOpen, setHeroBackUploadOpen] = useState(false);
  const [heroBackItems, setHeroBackItems] = useState<HeroImgUploadItem[]>([]);
  const [heroBackUploading, setHeroBackUploading] = useState(false);
  const heroBackInputRef = useRef<HTMLInputElement>(null);

  const [heroEvoUploadOpen, setHeroEvoUploadOpen] = useState(false);
  const [heroEvoItems, setHeroEvoItems] = useState<HeroImgUploadItem[]>([]);
  const [heroEvoUploading, setHeroEvoUploading] = useState(false);
  const heroEvoInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [hRes, sRes] = await Promise.all([
        fetch(`${BASE_URL}/hero-types?admin=1`).then(r => r.json()),
        fetch(`${BASE_URL}/skills`).then(r => r.json()),
      ]);
      if (hRes.success) setData(hRes.data);
      if (sRes.success) setAllSkills(sRes.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (r: HeroType) => {
    setEditing(r);
    form.setFieldsValue({ ...r, attributes: r.attributes ?? [] });
    setModalOpen(true);
  };
  const openSkillLevelModal = async (r: HeroType) => {
    setSkillLevelHero(r);
    setSkillLevelLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/hero-types/${r.id}/skill-levels`);
      const json = await res.json();
      skillLevelForm.setFieldsValue({
        entries: json.success ? json.data.map((e: any) => ({ skillId: e.skillId, requiredLevel: e.requiredLevel })) : [],
      });
    } finally { setSkillLevelLoading(false); }
  };

  const handleAutoDistributeSkillLevels = () => {
    const values = skillLevelForm.getFieldsValue();
    const entries: any[] = values.entries ?? [];
    if (entries.length === 0) return;
    // First 2 skills are initial (level 1), rest unlock at levels 2, 3, 4...
    const updated = entries.map((e: any, i: number) => ({
      ...e,
      requiredLevel: i < 2 ? 1 : i, // index 0,1 → level 1; index 2 → level 2; index 3 → level 3...
    }));
    skillLevelForm.setFieldsValue({ entries: updated });
    message.success(`已自动分配：前2个技能为初始技能（1级），其余技能按 2、3、4… 级顺序解锁`);
  };

  const handleSaveSkillLevels = async () => {
    if (!skillLevelHero) return;
    try {
      const values = await skillLevelForm.validateFields();
      const res = await fetch(`${BASE_URL}/hero-types/${skillLevelHero.id}/skill-levels`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries: values.entries ?? [] }),
      });
      const json = await res.json();
      if (json.success) { message.success('技能习得配置已保存'); setSkillLevelHero(null); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  const openLevelModal = async (r: HeroType) => {
    setLevelHero(r);
    setLevelLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/hero-types/${r.id}/levels`);
      const json = await res.json();
      levelForm.setFieldsValue({ levels: json.success ? json.data : [] });
    } finally { setLevelLoading(false); }
  };

  const handleSaveLevels = async () => {
    if (!levelHero) return;
    try {
      const values = await levelForm.validateFields();
      const res = await fetch(`${BASE_URL}/hero-types/${levelHero.id}/levels`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ levels: values.levels ?? [] }),
      });
      const json = await res.json();
      if (json.success) { message.success('等级配置已保存'); setLevelHero(null); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  const handleSubmit = async (values: any) => {
    try {
      const url = editing ? `${BASE_URL}/hero-types/${editing.id}` : `${BASE_URL}/hero-types`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(values) });
      const json = await res.json();
      if (json.success) { message.success('保存成功'); setModalOpen(false); load(); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  const handleDelete = async (id: number) => {
    const res = await fetch(`${BASE_URL}/hero-types/${id}`, { method: 'DELETE' });
    const json = await res.json();
    if (json.success) { message.success('删除成功'); load(); }
    else message.error(json.message);
  };

  // ---- Hero Type Import ----
  const openHeroImport = () => { setHeroImportRows([]); setHeroImportDone(null); setHeroImportProgress(0); setHeroImportOpen(true); };
  const handleHeroFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { setHeroImportRows(parseHeroCSV(ev.target?.result as string)); setHeroImportDone(null); };
    reader.readAsText(file, 'utf-8'); e.target.value = '';
  };
  const handleHeroImport = async () => {
    const valid = heroImportRows.filter(r => !r._error);
    if (!valid.length) { message.warning('没有可导入的有效数据'); return; }
    setHeroImportLoading(true); setHeroImportProgress(0);
    let ok = 0; let fail = 0;
    for (let i = 0; i < valid.length; i++) {
      const r = valid[i];
      try {
        const res = await fetch(`${BASE_URL}/hero-types`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: r.code, name: r.name, nameEn: r.nameEn, attributes: r.attributes, baseHp: r.baseHp, baseAtk: r.baseAtk, baseDef: r.baseDef, baseSpeed: r.baseSpeed, specialty: r.specialty, description: r.description, displayOrder: r.displayOrder, isActive: true }),
        });
        const json = await res.json();
        if (json.success) ok++; else fail++;
      } catch { fail++; }
      setHeroImportProgress(Math.round(((i + 1) / valid.length) * 100));
    }
    setHeroImportLoading(false); setHeroImportDone({ ok, fail });
    if (ok > 0) load();
  };

  // ---- Hero Level Import ----
  const openLevelImport = () => { setLevelImportRows([]); setLevelImportDone(null); setLevelImportProgress(0); setLevelImportOpen(true); };
  const handleLevelFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { setLevelImportRows(parseHeroLevelCSV(ev.target?.result as string)); setLevelImportDone(null); };
    reader.readAsText(file, 'utf-8'); e.target.value = '';
  };
  const handleLevelImport = async () => {
    const valid = levelImportRows.filter(r => !r._error);
    if (!valid.length) { message.warning('没有可导入的有效数据'); return; }
    // group by heroCode
    const grouped = new Map<string, HeroLevelImportRow[]>();
    for (const row of valid) {
      const list = grouped.get(row.heroCode) ?? [];
      list.push(row); grouped.set(row.heroCode, list);
    }
    setLevelImportLoading(true); setLevelImportProgress(0);
    let ok = 0; let fail = 0; const total = grouped.size; let done = 0;
    const groupedEntries = Array.from(grouped.entries());
    for (const [heroCode, rows] of groupedEntries) {
      const hero = data.find(h => h.code === heroCode);
      if (!hero) { fail += rows.length; done++; setLevelImportProgress(Math.round((done / total) * 100)); continue; }
      const levels = rows.map((r: HeroLevelImportRow) => ({ level: r.level, requiredExp: r.requiredExp, hpBonus: r.hpBonus, atkBonus: r.atkBonus, defBonus: r.defBonus, speedBonus: r.speedBonus, isEvolution: r.isEvolution }));
      try {
        const res = await fetch(`${BASE_URL}/hero-types/${hero.id}/levels`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ levels }) });
        const json = await res.json();
        if (json.success) ok += rows.length; else fail += rows.length;
      } catch { fail += rows.length; }
      done++; setLevelImportProgress(Math.round((done / total) * 100));
    }
    setLevelImportLoading(false); setLevelImportDone({ ok, fail });
  };

  // ---- Hero Skill-Level Import ----
  const openSkillLevelImport = () => { setSkillLevelImportRows([]); setSkillLevelImportDone(null); setSkillLevelImportProgress(0); setSkillLevelImportOpen(true); };
  const handleSkillLevelFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { setSkillLevelImportRows(parseHeroSkillLevelCSV(ev.target?.result as string)); setSkillLevelImportDone(null); };
    reader.readAsText(file, 'utf-8'); e.target.value = '';
  };
  const handleSkillLevelImport = async () => {
    const valid = skillLevelImportRows.filter(r => !r._error);
    if (!valid.length) { message.warning('没有可导入的有效数据'); return; }
    // group by heroCode
    const grouped = new Map<string, HeroSkillLevelImportRow[]>();
    for (const row of valid) {
      const list = grouped.get(row.heroCode) ?? [];
      list.push(row); grouped.set(row.heroCode, list);
    }
    setSkillLevelImportLoading(true); setSkillLevelImportProgress(0);
    let ok = 0; let fail = 0; const total = grouped.size; let done = 0;
    const groupedEntries = Array.from(grouped.entries());
    for (const [heroCode, rows] of groupedEntries) {
      const hero = data.find(h => h.code === heroCode);
      if (!hero) { fail += rows.length; done++; setSkillLevelImportProgress(Math.round((done / total) * 100)); continue; }
      const entries = rows.map((r: HeroSkillLevelImportRow) => {
        const skill = allSkills.find(s => s.name.trim() === r.skillName.trim());
        return skill ? { skillId: skill.id, requiredLevel: r.requiredLevel } : null;
      }).filter(Boolean);
      try {
        const res = await fetch(`${BASE_URL}/hero-types/${hero.id}/skill-levels`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ entries }) });
        const json = await res.json();
        if (json.success) ok += rows.length; else fail += rows.length;
      } catch { fail += rows.length; }
      done++; setSkillLevelImportProgress(Math.round((done / total) * 100));
    }
    setSkillLevelImportLoading(false); setSkillLevelImportDone({ ok, fail });
    if (ok > 0) load();
  };

  // ---- Hero bulk image upload handlers ----
  const matchHeroByCode = (filename: string) => {
    const base = filename.replace(/\.[^.]+$/, '').trim();
    return data.find(h => h.code.toLowerCase() === base.toLowerCase()) ?? null;
  };

  // Front upload
  const openHeroFrontUpload = () => { setHeroFrontItems([]); setHeroFrontUploadOpen(true); };
  const handleHeroFrontFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const items: HeroImgUploadItem[] = files.map(file => {
      const hero = matchHeroByCode(file.name);
      return { file, filename: file.name, heroCode: hero?.code ?? null, heroName: hero?.name ?? null, heroId: hero?.id ?? null, status: hero ? 'pending' : 'unmatched' };
    });
    setHeroFrontItems(items); e.target.value = '';
  };
  const handleHeroFrontUpload = async () => {
    const toUpload = heroFrontItems.filter(t => t.status === 'pending');
    if (!toUpload.length) { message.warning('没有已匹配的图片'); return; }
    setHeroFrontUploading(true);
    const updated = [...heroFrontItems];
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status !== 'pending') continue;
      updated[i] = { ...updated[i], status: 'uploading' }; setHeroFrontItems([...updated]);
      try {
        const fd = new FormData(); fd.append('file', updated[i].file); fd.append('name', updated[i].heroCode!);
        const r1 = await fetch(`${BASE_URL}/hero-types/upload-avatar`, { method: 'POST', body: fd });
        const j1 = await r1.json(); if (!j1.success) throw new Error(j1.message);
        const avatarUrl = j1.data.url;
        const r2 = await fetch(`${BASE_URL}/hero-types/${updated[i].heroId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ avatarUrl }),
        });
        const j2 = await r2.json(); if (!j2.success) throw new Error(j2.message);
        updated[i] = { ...updated[i], status: 'done', url: avatarUrl };
      } catch (e: any) { updated[i] = { ...updated[i], status: 'error', error: e.message }; }
      setHeroFrontItems([...updated]);
    }
    setHeroFrontUploading(false); load();
  };

  // Back upload
  const openHeroBackUpload = () => { setHeroBackItems([]); setHeroBackUploadOpen(true); };
  const handleHeroBackFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const items: HeroImgUploadItem[] = files.map(file => {
      const hero = matchHeroByCode(file.name);
      return { file, filename: file.name, heroCode: hero?.code ?? null, heroName: hero?.name ?? null, heroId: hero?.id ?? null, status: hero ? 'pending' : 'unmatched' };
    });
    setHeroBackItems(items); e.target.value = '';
  };
  const handleHeroBackUpload = async () => {
    const toUpload = heroBackItems.filter(t => t.status === 'pending');
    if (!toUpload.length) { message.warning('没有已匹配的图片'); return; }
    setHeroBackUploading(true);
    const updated = [...heroBackItems];
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status !== 'pending') continue;
      updated[i] = { ...updated[i], status: 'uploading' }; setHeroBackItems([...updated]);
      try {
        const fd = new FormData(); fd.append('file', updated[i].file); fd.append('name', `back-${updated[i].heroCode}`);
        const r1 = await fetch(`${BASE_URL}/hero-types/upload-card-back`, { method: 'POST', body: fd });
        const j1 = await r1.json(); if (!j1.success) throw new Error(j1.message);
        const cardBackUrl = j1.data.url;
        const r2 = await fetch(`${BASE_URL}/hero-types/${updated[i].heroId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cardBackUrl }),
        });
        const j2 = await r2.json(); if (!j2.success) throw new Error(j2.message);
        updated[i] = { ...updated[i], status: 'done', url: cardBackUrl };
      } catch (e: any) { updated[i] = { ...updated[i], status: 'error', error: e.message }; }
      setHeroBackItems([...updated]);
    }
    setHeroBackUploading(false); load();
  };

  // Evolution upload — filename: HOU_YI_lv10.png → heroCode=HOU_YI, level=10
  const parseEvoFilename = (filename: string): { heroCode: string; level: number } | null => {
    const m = filename.replace(/\.[^.]+$/, '').match(/^(.+)_lv(\d+)$/i);
    if (!m) return null;
    return { heroCode: m[1], level: Number(m[2]) };
  };
  const openHeroEvoUpload = () => { setHeroEvoItems([]); setHeroEvoUploadOpen(true); };
  const handleHeroEvoFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const items: HeroImgUploadItem[] = files.map(file => {
      const parsed = parseEvoFilename(file.name);
      const hero = parsed ? (data.find(h => h.code.toLowerCase() === parsed.heroCode.toLowerCase()) ?? null) : null;
      return {
        file, filename: file.name,
        heroCode: hero?.code ?? parsed?.heroCode ?? null, heroName: hero?.name ?? null, heroId: hero?.id ?? null,
        level: parsed?.level, status: (hero && parsed) ? 'pending' : 'unmatched',
      };
    });
    setHeroEvoItems(items); e.target.value = '';
  };
  const handleHeroEvoUpload = async () => {
    const toUpload = heroEvoItems.filter(t => t.status === 'pending');
    if (!toUpload.length) { message.warning('没有已匹配的图片'); return; }
    setHeroEvoUploading(true);
    const updated = [...heroEvoItems];
    // Cache levels per hero to avoid repeated fetches
    const levelsCache: Record<number, HeroLevel[]> = {};
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status !== 'pending') continue;
      updated[i] = { ...updated[i], status: 'uploading' }; setHeroEvoItems([...updated]);
      try {
        const heroId = updated[i].heroId!;
        const targetLevel = updated[i].level!;
        // Fetch levels (cached)
        if (!levelsCache[heroId]) {
          const r = await fetch(`${BASE_URL}/hero-types/${heroId}/levels`);
          const j = await r.json();
          levelsCache[heroId] = j.success ? j.data : [];
        }
        const levels = levelsCache[heroId];
        const lvIdx = levels.findIndex((l: HeroLevel) => l.level === targetLevel);
        if (lvIdx === -1) throw new Error(`英雄 ${updated[i].heroCode} 不存在 lv${targetLevel} 等级记录`);
        // Upload image
        const fd = new FormData(); fd.append('file', updated[i].file); fd.append('name', `evo-${updated[i].heroCode}-lv${targetLevel}`);
        const r1 = await fetch(`${BASE_URL}/hero-types/upload-avatar`, { method: 'POST', body: fd });
        const j1 = await r1.json(); if (!j1.success) throw new Error(j1.message);
        const evolutionImageUrl = j1.data.url;
        // Update level record
        levels[lvIdx] = { ...levels[lvIdx], evolutionImageUrl };
        levelsCache[heroId] = levels;
        const r2 = await fetch(`${BASE_URL}/hero-types/${heroId}/levels`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ levels }),
        });
        const j2 = await r2.json(); if (!j2.success) throw new Error(j2.message);
        updated[i] = { ...updated[i], status: 'done', url: evolutionImageUrl };
      } catch (e: any) { updated[i] = { ...updated[i], status: 'error', error: e.message }; }
      setHeroEvoItems([...updated]);
    }
    setHeroEvoUploading(false); load();
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 50 },
    { title: '编码', dataIndex: 'code', width: 90 },
    {
      title: '名称', key: 'name', width: 140,
      render: (_: any, r: HeroType) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 600 }}>{r.name}</span>
          {r.nameEn && <span style={{ fontSize: 11, color: '#888' }}>{r.nameEn}</span>}
        </Space>
      ),
    },
    {
      title: '属性', dataIndex: 'attributes', width: 120,
      render: (attrs: string[]) => (attrs ?? []).map(a => <Tag key={a} style={{ margin: '0 2px 2px 0' }}>{a}</Tag>),
    },
    { title: '基础HP', dataIndex: 'baseHp', width: 70 },
    { title: '基础ATK', dataIndex: 'baseAtk', width: 70 },
    { title: '基础DEF', dataIndex: 'baseDef', width: 70 },
    { title: '基础SPD', dataIndex: 'baseSpeed', width: 70 },
    {
      title: '技能习得', key: 'skills', width: 90,
      render: (_: any, r: HeroType) => r.skillLevelCount > 0
        ? <Tag color="blue">{r.skillLevelCount} 个</Tag>
        : <span style={{ color: '#999', fontSize: 12 }}>—</span>,
    },
    { title: '排序', dataIndex: 'displayOrder', width: 55 },
    {
      title: '状态', dataIndex: 'isActive', width: 70,
      render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '操作', width: 190,
      render: (_: any, r: HeroType) => (
        <Space>
          <Button size="small" onClick={() => openLevelModal(r)}>等级配置</Button>
          <Button size="small" onClick={() => openSkillLevelModal(r)}>技能习得</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="英雄类型"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
          <Button icon={<UploadOutlined />} onClick={openHeroImport}>英雄导入</Button>
          <Button icon={<UploadOutlined />} onClick={openLevelImport}>等级导入</Button>
          <Button icon={<UploadOutlined />} onClick={openSkillLevelImport}>技能导入</Button>
          <Button icon={<UploadOutlined />} onClick={openHeroFrontUpload}>批量正面图</Button>
          <Button icon={<UploadOutlined />} onClick={openHeroBackUpload}>批量反面图</Button>
          <Button icon={<UploadOutlined />} onClick={openHeroEvoUpload}>批量进化图</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增</Button>
        </Space>
      }
    >
      {/* Hidden file inputs */}
      <input ref={heroFileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleHeroFileChange} />
      <input ref={levelFileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleLevelFileChange} />
      <input ref={skillLevelFileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleSkillLevelFileChange} />
      <input ref={heroFrontInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleHeroFrontFilesChange} />
      <input ref={heroBackInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleHeroBackFilesChange} />
      <input ref={heroEvoInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleHeroEvoFilesChange} />

      <Table rowKey="id" dataSource={data} columns={columns} loading={loading} size="small" pagination={{ pageSize: 20 }} scroll={{ x: 1000 }} />

      <Modal title={editing ? '编辑英雄类型' : '新增英雄类型'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={() => form.submit()} okText="保存" width={600}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={12}>
            <Col span={6}><Form.Item name="code" label="编码" rules={[{ required: true }]}><Input placeholder="warrior" /></Form.Item></Col>
            <Col span={9}><Form.Item name="name" label="中文名" rules={[{ required: true }]}><Input placeholder="战士" /></Form.Item></Col>
            <Col span={9}><Form.Item name="nameEn" label="英文名"><Input placeholder="Warrior" /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="attributes" label="属性标签（可多选）">
                <Select mode="tags" placeholder="选择或输入属性" tokenSeparators={[',']}>
                  {ATTR_PRESETS.map(a => <Select.Option key={a} value={a}>{a}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}><Form.Item name="displayOrder" label="排序" initialValue={10}><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={6}>
              <Form.Item name="isActive" label="状态" initialValue={true}>
                <Select><Select.Option value={true}>启用</Select.Option><Select.Option value={false}>禁用</Select.Option></Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="specialty" label="特长描述"><Input placeholder="铁甲防御，擅长消耗战" /></Form.Item>
          <Form.Item name="description" label="详细描述"><Input.TextArea rows={2} /></Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="avatarUrl" label="头像">
                <Form.Item shouldUpdate={(prev, cur) => prev.avatarUrl !== cur.avatarUrl || prev.code !== cur.code} noStyle>
                  {({ getFieldValue, setFieldValue }) => (
                    <AvatarUpload
                      uploadUrl={`${BASE_URL}/hero-types/upload-avatar`}
                      nameField={getFieldValue('code') || `hero-${Date.now()}`}
                      value={getFieldValue('avatarUrl')}
                      onChange={url => setFieldValue('avatarUrl', url)}
                    />
                  )}
                </Form.Item>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="cardBackUrl" label="战斗卡背图">
                <Form.Item shouldUpdate={(prev, cur) => prev.cardBackUrl !== cur.cardBackUrl || prev.code !== cur.code} noStyle>
                  {({ getFieldValue, setFieldValue }) => (
                    <AvatarUpload
                      uploadUrl={`${BASE_URL}/hero-types/upload-card-back`}
                      nameField={getFieldValue('code') || `hero-${Date.now()}`}
                      value={getFieldValue('cardBackUrl')}
                      onChange={url => setFieldValue('cardBackUrl', url)}
                    />
                  )}
                </Form.Item>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}><Form.Item name="baseHp" label="基础HP" initialValue={100}><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
            <Col span={6}><Form.Item name="baseAtk" label="基础ATK" initialValue={15}><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
            <Col span={6}><Form.Item name="baseDef" label="基础DEF" initialValue={8}><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
            <Col span={6}><Form.Item name="baseSpeed" label="基础SPD" initialValue={10}><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>

      {/* 等级配置 Modal */}
      <Modal
        title={levelHero ? `等级配置 — ${levelHero.name}` : ''}
        open={!!levelHero}
        onCancel={() => setLevelHero(null)}
        onOk={handleSaveLevels}
        okText="保存"
        width={780}
        confirmLoading={levelLoading}
      >
        <Alert
          type="info"
          style={{ marginBottom: 12 }}
          message="配置说明"
          description="每行对应一个升级档位（等级值从2开始）。「所需经验」为从上一级升到该级所需的经验量。升级后各属性加成会叠加到英雄当前属性上。勾选「进化」可上传该等级的进化图片，游戏中将替换英雄立绘。"
        />
        <Form form={levelForm} layout="vertical">
          <Form.List name="levels">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <div key={key} style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: '10px 12px 0', marginBottom: 10 }}>
                    <Row gutter={8} align="middle">
                      <Col span={3}>
                        <Form.Item name={[name, 'level']} label="等级" rules={[{ required: true }]}>
                          <InputNumber min={2} max={99} style={{ width: '100%' }} placeholder="2" />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item name={[name, 'requiredExp']} label="所需经验" rules={[{ required: true }]}>
                          <InputNumber min={0} style={{ width: '100%' }} placeholder="100" />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item name={[name, 'hpBonus']} label="HP+" initialValue={0}>
                          <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item name={[name, 'atkBonus']} label="ATK+" initialValue={0}>
                          <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item name={[name, 'defBonus']} label="DEF+" initialValue={0}>
                          <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item name={[name, 'speedBonus']} label="SPD+" initialValue={0}>
                          <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item name={[name, 'isEvolution']} label="进化" valuePropName="checked" initialValue={false}>
                          <Switch size="small" />
                        </Form.Item>
                      </Col>
                      <Col span={2} style={{ paddingTop: 4 }}>
                        <Button danger size="small" onClick={() => remove(name)} style={{ marginTop: 28 }}>删</Button>
                      </Col>
                    </Row>
                    <Form.Item shouldUpdate noStyle>
                      {({ getFieldValue }) => getFieldValue(['levels', name, 'isEvolution']) ? (
                        <Form.Item name={[name, 'evolutionImageUrl']} label="进化图片">
                          <Form.Item shouldUpdate noStyle>
                            {({ getFieldValue: gfv, setFieldValue: sfv }) => (
                              <AvatarUpload
                                uploadUrl={`${BASE_URL}/hero-types/upload-avatar`}
                                nameField={`${levelHero?.code ?? 'hero'}-lv${getFieldValue(['levels', name, 'level']) ?? name}`}
                                value={gfv(['levels', name, 'evolutionImageUrl'])}
                                onChange={url => sfv(['levels', name, 'evolutionImageUrl'], url)}
                              />
                            )}
                          </Form.Item>
                        </Form.Item>
                      ) : null}
                    </Form.Item>
                  </div>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ hpBonus: 0, atkBonus: 0, defBonus: 0, speedBonus: 0, isEvolution: false })} block>
                  添加等级档位（共 {fields.length} 个）
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* 技能习得配置 Modal */}
      <Modal
        title={skillLevelHero ? `技能习得 — ${skillLevelHero.name}` : ''}
        open={!!skillLevelHero}
        onCancel={() => setSkillLevelHero(null)}
        onOk={handleSaveSkillLevels}
        okText="保存"
        width={560}
        confirmLoading={skillLevelLoading}
      >
        {allSkills.length === 0
          ? <Alert type="warning" message="暂无技能，请先在「技能管理」Tab创建技能" />
          : (
            <Form form={skillLevelForm} layout="vertical">
              <Alert
                type="info"
                style={{ marginBottom: 12 }}
                message="技能习得规则"
                description={
                  <ul style={{ margin: 0, paddingLeft: 16, lineHeight: 1.8 }}>
                    <li>必须配置至少 <b>2个初始技能</b>（习得等级 ≤ 1），否则无法开始战斗</li>
                    <li>可配置任意数量的升级技能；玩家在任意时刻最多拥有 <b>4个技能</b>（最近学到的4个）</li>
                  </ul>
                }
              />
              <Form.List name="entries">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name }) => (
                      <div key={key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                        <Form.Item name={[name, 'requiredLevel']} label="习得等级" rules={[{ required: true }]} style={{ width: 110, marginBottom: 0 }}>
                          <InputNumber min={1} max={50} style={{ width: '100%' }} placeholder="1" />
                        </Form.Item>
                        <Form.Item name={[name, 'skillId']} label="技能" rules={[{ required: true }]} style={{ flex: 1, marginBottom: 0 }}>
                          <Select placeholder="选择技能">
                            {allSkills.map(s => (
                              <Select.Option key={s.id} value={s.id}>
                                <Space>
                                  <Tag color={s.skillType === 'damage' ? 'red' : s.skillType === 'status' ? 'purple' : s.skillType === 'heal' ? 'green' : 'orange'} style={{ marginRight: 0 }}>
                                    {s.skillType === 'damage' ? '⚔' : s.skillType === 'status' ? '✨' : s.skillType === 'heal' ? '💚' : '⚔✨'}
                                  </Tag>
                                  {s.name}
                                  {s.nameEn && <span style={{ color: '#999', fontSize: 11 }}>{s.nameEn}</span>}
                                </Space>
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Button danger size="small" onClick={() => remove(name)} style={{ marginTop: 28 }}>删除</Button>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()} style={{ flex: 1 }}>
                        添加技能（共 {fields.length} 个）
                      </Button>
                      <Button
                        onClick={handleAutoDistributeSkillLevels}
                        title="前2个设为1级初始技能，其余依次设为2、3、4…级"
                      >
                        🎯 一键顺序分配等级
                      </Button>
                    </div>
                  </>
                )}
              </Form.List>
            </Form>
          )
        }
      </Modal>

      {/* 英雄类型批量导入 Modal */}
      <Modal
        title="批量导入英雄类型"
        open={heroImportOpen}
        onCancel={() => setHeroImportOpen(false)}
        footer={[
          <Button key="tpl" icon={<DownloadOutlined />} onClick={downloadHeroTemplate}>下载模板</Button>,
          <Button key="pick" icon={<UploadOutlined />} onClick={() => heroFileInputRef.current?.click()}>选择CSV文件</Button>,
          <Button key="imp" type="primary" loading={heroImportLoading} disabled={!heroImportRows.length || !!heroImportDone} onClick={handleHeroImport}>开始导入</Button>,
          <Button key="close" onClick={() => setHeroImportOpen(false)}>关闭</Button>,
        ]}
        width={700}
      >
        <Alert type="info" style={{ marginBottom: 12 }} message="CSV格式" description={`列顺序：${HERO_CSV_HEADERS.join(' | ')}。attributes用"|"分隔多个属性（如 火|金）。`} />
        {heroImportDone && <Alert type={heroImportDone.fail === 0 ? 'success' : 'warning'} message={`导入完成：成功 ${heroImportDone.ok} 条，失败 ${heroImportDone.fail} 条`} style={{ marginBottom: 8 }} />}
        {heroImportLoading && <Progress percent={heroImportProgress} style={{ marginBottom: 8 }} />}
        {heroImportRows.length > 0 && (
          <Table
            rowKey="_line" size="small" pagination={{ pageSize: 10 }}
            dataSource={heroImportRows}
            columns={[
              { title: '行', dataIndex: '_line', width: 45 },
              { title: '编码', dataIndex: 'code', width: 90 },
              { title: '名称', dataIndex: 'name', width: 100 },
              { title: '属性', dataIndex: 'attributes', width: 100, render: (a: string[]) => a.join('|') },
              { title: 'HP/ATK/DEF/SPD', key: 'stats', width: 150, render: (_: any, r: HeroImportRow) => `${r.baseHp}/${r.baseAtk}/${r.baseDef}/${r.baseSpeed}` },
              { title: '错误', dataIndex: '_error', render: (e: string) => e ? <Tag color="red">{e}</Tag> : <Tag color="green">OK</Tag> },
            ]}
          />
        )}
      </Modal>

      {/* 英雄等级批量导入 Modal */}
      <Modal
        title="批量导入英雄等级数值"
        open={levelImportOpen}
        onCancel={() => setLevelImportOpen(false)}
        footer={[
          <Button key="tpl" icon={<DownloadOutlined />} onClick={downloadHeroLevelTemplate}>下载模板</Button>,
          <Button key="pick" icon={<UploadOutlined />} onClick={() => levelFileInputRef.current?.click()}>选择CSV文件</Button>,
          <Button key="imp" type="primary" loading={levelImportLoading} disabled={!levelImportRows.length || !!levelImportDone} onClick={handleLevelImport}>开始导入</Button>,
          <Button key="close" onClick={() => setLevelImportOpen(false)}>关闭</Button>,
        ]}
        width={720}
      >
        <Alert type="info" style={{ marginBottom: 12 }} message="CSV格式" description={`列顺序：${HERO_LEVEL_CSV_HEADERS.join(' | ')}。heroCode必须与英雄类型的code字段一致。每位英雄的数据会整体替换（PUT）。`} />
        <Alert type="warning" style={{ marginBottom: 12 }} message="注意" description="导入会覆盖对应英雄已有的全部等级配置。请确保CSV中包含该英雄所有等级的数据（level从2开始）。" />
        {levelImportDone && <Alert type={levelImportDone.fail === 0 ? 'success' : 'warning'} message={`导入完成：成功 ${levelImportDone.ok} 条，失败 ${levelImportDone.fail} 条`} style={{ marginBottom: 8 }} />}
        {levelImportLoading && <Progress percent={levelImportProgress} style={{ marginBottom: 8 }} />}
        {levelImportRows.length > 0 && (
          <Table
            rowKey="_line" size="small" pagination={{ pageSize: 10 }}
            dataSource={levelImportRows}
            columns={[
              { title: '行', dataIndex: '_line', width: 45 },
              { title: '英雄Code', dataIndex: 'heroCode', width: 100 },
              { title: '等级', dataIndex: 'level', width: 55 },
              { title: '所需EXP', dataIndex: 'requiredExp', width: 80 },
              { title: 'HP+', dataIndex: 'hpBonus', width: 55 },
              { title: 'ATK+', dataIndex: 'atkBonus', width: 55 },
              { title: 'DEF+', dataIndex: 'defBonus', width: 55 },
              { title: 'SPD+', dataIndex: 'speedBonus', width: 55 },
              { title: '进化', dataIndex: 'isEvolution', width: 55, render: (v: boolean) => v ? <Tag color="gold">是</Tag> : '—' },
              { title: '状态', dataIndex: '_error', render: (e: string) => e ? <Tag color="red">{e}</Tag> : <Tag color="green">OK</Tag> },
            ]}
          />
        )}
      </Modal>

      {/* 英雄技能习得批量导入 Modal */}
      <Modal
        title="批量导入英雄技能习得"
        open={skillLevelImportOpen}
        onCancel={() => setSkillLevelImportOpen(false)}
        footer={[
          <Button key="tpl" icon={<DownloadOutlined />} onClick={downloadHeroSkillLevelTemplate}>下载模板</Button>,
          <Button key="pick" icon={<UploadOutlined />} onClick={() => skillLevelFileInputRef.current?.click()}>选择CSV文件</Button>,
          <Button key="imp" type="primary" loading={skillLevelImportLoading} disabled={!skillLevelImportRows.length || !!skillLevelImportDone} onClick={handleSkillLevelImport}>开始导入</Button>,
          <Button key="close" onClick={() => setSkillLevelImportOpen(false)}>关闭</Button>,
        ]}
        width={600}
      >
        <Alert type="info" style={{ marginBottom: 12 }} message="CSV格式" description={`列顺序：${HERO_SKILL_LEVEL_CSV_HEADERS.join(' | ')}。skillName需与技能库中的名称完全一致。每位英雄的技能配置会整体替换。`} />
        {skillLevelImportDone && <Alert type={skillLevelImportDone.fail === 0 ? 'success' : 'warning'} message={`导入完成：成功 ${skillLevelImportDone.ok} 条，失败 ${skillLevelImportDone.fail} 条`} style={{ marginBottom: 8 }} />}
        {skillLevelImportLoading && <Progress percent={skillLevelImportProgress} style={{ marginBottom: 8 }} />}
        {skillLevelImportRows.length > 0 && (
          <Table
            rowKey="_line" size="small" pagination={{ pageSize: 10 }}
            dataSource={skillLevelImportRows}
            columns={[
              { title: '行', dataIndex: '_line', width: 45 },
              { title: '英雄Code', dataIndex: 'heroCode', width: 100 },
              { title: '技能名称', dataIndex: 'skillName', width: 120 },
              { title: '习得等级', dataIndex: 'requiredLevel', width: 80 },
              {
                title: '技能匹配', key: 'match', width: 90,
                render: (_: any, r: HeroSkillLevelImportRow) => allSkills.find(s => s.name.trim() === r.skillName.trim())
                  ? <Tag color="green">已找到</Tag>
                  : <Tag color="orange">未找到</Tag>,
              },
              { title: '状态', dataIndex: '_error', render: (e: string) => e ? <Tag color="red">{e}</Tag> : <Tag color="green">OK</Tag> },
            ]}
          />
        )}
      </Modal>
      {/* 英雄正面批量上传 */}
      <Modal
        title="批量上传英雄正面立绘"
        open={heroFrontUploadOpen}
        onCancel={() => { if (!heroFrontUploading) setHeroFrontUploadOpen(false); }}
        footer={[
          <Button key="sel" icon={<UploadOutlined />} onClick={() => heroFrontInputRef.current?.click()} disabled={heroFrontUploading}>选择图片（可多选）</Button>,
          <Button key="go" type="primary" loading={heroFrontUploading}
            disabled={heroFrontItems.filter(t => t.status === 'pending').length === 0}
            onClick={handleHeroFrontUpload}>
            上传 {heroFrontItems.filter(t => t.status === 'pending').length} 张已匹配图片
          </Button>,
          <Button key="close" onClick={() => setHeroFrontUploadOpen(false)} disabled={heroFrontUploading}>关闭</Button>,
        ]}
        width={680}
      >
        <Alert style={{ marginBottom: 12 }} type="info" message="文件名规则" description="文件名须与英雄编码完全匹配（不区分大小写），例如：HOU_YI.png、NU_WA.png。上传后更新英雄正面头像（avatarUrl）。" />
        {heroFrontItems.length > 0 ? (
          <Table rowKey="filename" size="small" dataSource={heroFrontItems} pagination={{ pageSize: 15 }}
            columns={[
              { title: '文件名', dataIndex: 'filename', ellipsis: true },
              { title: '匹配英雄', key: 'hero', render: (_: any, r: HeroImgUploadItem) => r.heroName ? `${r.heroName} (${r.heroCode})` : <Tag color="orange">未找到</Tag> },
              { title: '预览', key: 'preview', render: (_: any, r: HeroImgUploadItem) => r.status === 'done' && r.url ? <img src={ossThumb(r.url, 200)} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }} /> : null },
              { title: '状态', key: 'status', render: (_: any, r: HeroImgUploadItem) => {
                if (r.status === 'done') return <Tag color="green">✓ 完成</Tag>;
                if (r.status === 'error') return <Tooltip title={r.error}><Tag color="red">✗ 失败</Tag></Tooltip>;
                if (r.status === 'uploading') return <Tag color="blue">上传中…</Tag>;
                if (r.status === 'unmatched') return <Tag color="orange">未匹配</Tag>;
                return <Tag>待上传</Tag>;
              }},
            ]}
          />
        ) : <div style={{ textAlign: 'center', color: '#aaa', padding: '40px 0' }}>请选择图片文件</div>}
      </Modal>

      {/* 英雄反面批量上传 */}
      <Modal
        title="批量上传英雄反面立绘（战斗卡背）"
        open={heroBackUploadOpen}
        onCancel={() => { if (!heroBackUploading) setHeroBackUploadOpen(false); }}
        footer={[
          <Button key="sel" icon={<UploadOutlined />} onClick={() => heroBackInputRef.current?.click()} disabled={heroBackUploading}>选择图片（可多选）</Button>,
          <Button key="go" type="primary" loading={heroBackUploading}
            disabled={heroBackItems.filter(t => t.status === 'pending').length === 0}
            onClick={handleHeroBackUpload}>
            上传 {heroBackItems.filter(t => t.status === 'pending').length} 张已匹配图片
          </Button>,
          <Button key="close" onClick={() => setHeroBackUploadOpen(false)} disabled={heroBackUploading}>关闭</Button>,
        ]}
        width={680}
      >
        <Alert style={{ marginBottom: 12 }} type="info" message="文件名规则" description="文件名须与英雄编码完全匹配（不区分大小写），例如：HOU_YI.png。反面图应为正面左右镜像（bash 生成英雄图片.sh back 自动生成）。上传后更新战斗卡背（cardBackUrl）。" />
        {heroBackItems.length > 0 ? (
          <Table rowKey="filename" size="small" dataSource={heroBackItems} pagination={{ pageSize: 15 }}
            columns={[
              { title: '文件名', dataIndex: 'filename', ellipsis: true },
              { title: '匹配英雄', key: 'hero', render: (_: any, r: HeroImgUploadItem) => r.heroName ? `${r.heroName} (${r.heroCode})` : <Tag color="orange">未找到</Tag> },
              { title: '预览', key: 'preview', render: (_: any, r: HeroImgUploadItem) => r.status === 'done' && r.url ? <img src={ossThumb(r.url, 200)} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }} /> : null },
              { title: '状态', key: 'status', render: (_: any, r: HeroImgUploadItem) => {
                if (r.status === 'done') return <Tag color="green">✓ 完成</Tag>;
                if (r.status === 'error') return <Tooltip title={r.error}><Tag color="red">✗ 失败</Tag></Tooltip>;
                if (r.status === 'uploading') return <Tag color="blue">上传中…</Tag>;
                if (r.status === 'unmatched') return <Tag color="orange">未匹配</Tag>;
                return <Tag>待上传</Tag>;
              }},
            ]}
          />
        ) : <div style={{ textAlign: 'center', color: '#aaa', padding: '40px 0' }}>请选择图片文件</div>}
      </Modal>

      {/* 英雄进化阶段批量上传 */}
      <Modal
        title="批量上传英雄进化阶段图"
        open={heroEvoUploadOpen}
        onCancel={() => { if (!heroEvoUploading) setHeroEvoUploadOpen(false); }}
        footer={[
          <Button key="sel" icon={<UploadOutlined />} onClick={() => heroEvoInputRef.current?.click()} disabled={heroEvoUploading}>选择图片（可多选）</Button>,
          <Button key="go" type="primary" loading={heroEvoUploading}
            disabled={heroEvoItems.filter(t => t.status === 'pending').length === 0}
            onClick={handleHeroEvoUpload}>
            上传 {heroEvoItems.filter(t => t.status === 'pending').length} 张已匹配图片
          </Button>,
          <Button key="close" onClick={() => setHeroEvoUploadOpen(false)} disabled={heroEvoUploading}>关闭</Button>,
        ]}
        width={720}
      >
        <Alert style={{ marginBottom: 12 }} type="info" message="文件名规则" description="格式：英雄编码_lv等级.png，例如：HOU_YI_lv10.png、NU_WA_lv20.png。支持 lv10/lv20/lv30/lv40。对应等级必须已在等级配置中存在。" />
        {heroEvoItems.length > 0 ? (
          <Table rowKey="filename" size="small" dataSource={heroEvoItems} pagination={{ pageSize: 15 }}
            columns={[
              { title: '文件名', dataIndex: 'filename', ellipsis: true },
              { title: '匹配英雄', key: 'hero', render: (_: any, r: HeroImgUploadItem) => r.heroName ? `${r.heroName} (${r.heroCode})` : <Tag color="orange">未找到</Tag> },
              { title: '等级', key: 'lv', render: (_: any, r: HeroImgUploadItem) => r.level ? <Tag color="purple">Lv {r.level}</Tag> : <Tag color="orange">未识别</Tag> },
              { title: '预览', key: 'preview', render: (_: any, r: HeroImgUploadItem) => r.status === 'done' && r.url ? <img src={ossThumb(r.url, 200)} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }} /> : null },
              { title: '状态', key: 'status', render: (_: any, r: HeroImgUploadItem) => {
                if (r.status === 'done') return <Tag color="green">✓ 完成</Tag>;
                if (r.status === 'error') return <Tooltip title={r.error}><Tag color="red">✗ 失败</Tag></Tooltip>;
                if (r.status === 'uploading') return <Tag color="blue">上传中…</Tag>;
                if (r.status === 'unmatched') return <Tag color="orange">未匹配</Tag>;
                return <Tag>待上传</Tag>;
              }},
            ]}
          />
        ) : <div style={{ textAlign: 'center', color: '#aaa', padding: '40px 0' }}>请选择图片文件</div>}
      </Modal>
    </Card>
  );
};

// ==================== Boss Tab ====================
// 山海经五行属性体系（金木水火土五行 + 雷风毒三角 + 混沌特殊）
const ATTR_PRESETS = ['无', '金', '木', '水', '火', '土', '雷', '风', '毒', '混沌'];

const CSV_HEADERS = ['code','name','nameEn','rarity','difficulty','hp','atk','def','speed','expReward','cooldownHours','attributes','description'];
const CSV_TEMPLATE_ROW = ['BOSS001','九尾狐','Nine-Tailed Fox','UR','5','800','65','40','18','500','24','毒','山中有一种野兽，形状如狐狸，长着九条尾巴'];

interface ImportRow {
  _line: number;
  code: string; name: string; nameEn?: string; rarity: string; difficulty: number;
  hp: number; atk: number; def: number; speed: number; expReward: number; cooldownSeconds: number;
  attributes: string[]; description?: string;
  _error?: string;
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let cur = '';
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQuote = !inQuote; continue; }
    if (ch === ',' && !inQuote) { result.push(cur.trim()); cur = ''; continue; }
    cur += ch;
  }
  result.push(cur.trim());
  return result;
}

function parseCSV(text: string): ImportRow[] {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter(l => l.trim());
  // skip header row
  const dataLines = lines[0].toLowerCase().startsWith('code') ? lines.slice(1) : lines;
  return dataLines.map((line, idx) => {
    const cols = parseCSVLine(line);
    const [code, name, nameEn, rarity, difficulty, hp, atk, def, speed, expReward, cooldownHours, attributes, description] = cols;
    const errors: string[] = [];
    if (!code) errors.push('缺少编码');
    if (!name) errors.push('缺少名称');
    const diff = Number(difficulty) || 1;
    if (diff < 1 || diff > 5) errors.push('难度须1-5');
    return {
      _line: idx + 2,
      code, name, nameEn: nameEn || undefined,
      rarity: rarity || 'N', difficulty: diff,
      hp: Number(hp) || 80, atk: Number(atk) || 12, def: Number(def) || 5, speed: Number(speed) || 10,
      expReward: Number(expReward) || 50, cooldownSeconds: Math.round((Number(cooldownHours) || 1) * 3600),
      attributes: attributes ? attributes.split('|').map(a => a.trim()).filter(Boolean) : [],
      description: description || undefined,
      _error: errors.length ? errors.join('；') : undefined,
    };
  }).filter(r => r.code || r.name);
}

function downloadTemplate() {
  const csv = [CSV_HEADERS.join(','), CSV_TEMPLATE_ROW.join(',')].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'boss_import_template.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ---- Boss掉落CSV导入工具函数 ----
const LOOT_CSV_HEADERS = ['bossCode','itemName','dropChance','minQuantity','maxQuantity'];
const LOOT_CSV_TEMPLATE_ROW = ['SHJ_N_001','灵芝草','60','1','1'];

interface LootImportRow {
  _line: number;
  bossCode: string; itemName: string;
  dropChance: number; minQuantity: number; maxQuantity: number;
  _error?: string;
}

function parseLootCSV(text: string): LootImportRow[] {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter(l => l.trim());
  const dataLines = lines[0].toLowerCase().startsWith('bosscode') ? lines.slice(1) : lines;
  return dataLines.map((line, idx) => {
    const cols = parseCSVLine(line);
    const [bossCode, itemName, dropChance, minQuantity, maxQuantity] = cols;
    const errors: string[] = [];
    if (!bossCode) errors.push('缺少bossCode');
    if (!itemName) errors.push('缺少itemName');
    const chance = Number(dropChance);
    if (chance < 1 || chance > 100) errors.push('dropChance须1-100');
    return {
      _line: idx + 2, bossCode: bossCode?.trim(), itemName: itemName?.trim(),
      dropChance: chance || 30, minQuantity: Number(minQuantity) || 1, maxQuantity: Number(maxQuantity) || 1,
      _error: errors.length ? errors.join('；') : undefined,
    };
  }).filter(r => r.bossCode || r.itemName);
}

function downloadLootTemplate() {
  const csv = [LOOT_CSV_HEADERS.join(','), LOOT_CSV_TEMPLATE_ROW.join(',')].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'boss_loot_template.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ---- Boss二阶段 CSV 导入工具函数 ----
const PHASE2_CSV_HEADERS = ['bossCode','hp','atk','def','speed','attributes','description','skill1','skill2','skill3','skill4'];
const PHASE2_CSV_TEMPLATE_ROW = ['SHJ_SR_001','600','55','35','16','混沌|火','二阶段形态，混沌能量暴走','混沌破灭','太初之力','混沌诅咒','凤凰涅槃'];

interface Phase2ImportRow {
  _line: number;
  bossCode: string; hp: number; atk: number; def: number; speed: number;
  attributes: string[]; description?: string;
  skills: string[]; // skill names, resolved to IDs at import time
  _error?: string;
}

function parsePhase2CSV(text: string): Phase2ImportRow[] {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter(l => l.trim());
  const dataLines = lines[0].toLowerCase().startsWith('bosscode') ? lines.slice(1) : lines;
  return dataLines.map((line, idx) => {
    const cols = parseCSVLine(line);
    const [bossCode, hp, atk, def, speed, attributes, description, skill1, skill2, skill3, skill4] = cols;
    const errors: string[] = [];
    if (!bossCode) errors.push('缺少bossCode');
    if (!hp || Number(hp) <= 0) errors.push('hp须大于0');
    return {
      _line: idx + 2, bossCode: bossCode?.trim(),
      hp: Number(hp) || 0, atk: Number(atk) || 0, def: Number(def) || 0, speed: Number(speed) || 0,
      attributes: attributes ? attributes.split('|').map(a => a.trim()).filter(Boolean) : [],
      description: description?.trim() || undefined,
      skills: [skill1, skill2, skill3, skill4].map(s => s?.trim()).filter(Boolean),
      _error: errors.length ? errors.join('；') : undefined,
    };
  }).filter(r => r.bossCode);
}

function downloadPhase2Template() {
  const csv = [PHASE2_CSV_HEADERS.join(','), PHASE2_CSV_TEMPLATE_ROW.join(',')].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'boss_phase2_template.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ---- Boss技能 CSV 批量导入工具函数 ----
const BOSS_SKILL_CSV_HEADERS = ['bossCode', 'skill1', 'skill2', 'skill3', 'skill4'];
const BOSS_SKILL_CSV_TEMPLATE_ROW = ['SHJ_N_001', '雷啸', '烈焰冲击', '', ''];

interface BossSkillImportRow {
  _line: number;
  bossCode: string;
  skillNames: string[];
  _error?: string;
}

function parseBossSkillCSV(text: string): BossSkillImportRow[] {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/).filter(l => l.trim());
  const dataLines = lines[0].toLowerCase().startsWith('bosscode') ? lines.slice(1) : lines;
  return dataLines.map((line, idx) => {
    const cols = parseCSVLine(line);
    const [bossCode, skill1, skill2, skill3, skill4] = cols;
    const errors: string[] = [];
    if (!bossCode) errors.push('缺少bossCode');
    const skillNames = [skill1, skill2, skill3, skill4].map(s => s?.trim()).filter(Boolean);
    if (skillNames.length === 0) errors.push('至少填写1个技能名');
    return {
      _line: idx + 2,
      bossCode: bossCode?.trim(),
      skillNames,
      _error: errors.length ? errors.join('；') : undefined,
    };
  }).filter(r => r.bossCode);
}

function downloadBossSkillTemplate() {
  const csv = [BOSS_SKILL_CSV_HEADERS.join(','), BOSS_SKILL_CSV_TEMPLATE_ROW.join(',')].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'boss_skill_template.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ---- Boss二阶段技能 CSV 批量导入工具函数 ----
const BOSS_P2_SKILL_CSV_HEADERS = ['bossCode', 'skill1', 'skill2', 'skill3', 'skill4'];
const BOSS_P2_SKILL_CSV_TEMPLATE_ROW = ['SHJ_SSR_001', '混沌破灭', '太初之力', '混沌诅咒', '凤凰涅槃'];

interface BossP2SkillImportRow {
  _line: number;
  bossCode: string;
  skillNames: string[];
  _error?: string;
}

function parseBossP2SkillCSV(text: string): BossP2SkillImportRow[] {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/).filter(l => l.trim());
  const dataLines = lines[0].toLowerCase().startsWith('bosscode') ? lines.slice(1) : lines;
  return dataLines.map((line, idx) => {
    const cols = parseCSVLine(line);
    const [bossCode, skill1, skill2, skill3, skill4] = cols;
    const errors: string[] = [];
    if (!bossCode) errors.push('缺少bossCode');
    const skillNames = [skill1, skill2, skill3, skill4].map(s => s?.trim()).filter(Boolean);
    if (skillNames.length === 0) errors.push('至少填写1个技能名');
    return {
      _line: idx + 2,
      bossCode: bossCode?.trim(),
      skillNames,
      _error: errors.length ? errors.join('；') : undefined,
    };
  }).filter(r => r.bossCode);
}

function downloadBossP2SkillTemplate() {
  const csv = [BOSS_P2_SKILL_CSV_HEADERS.join(','), BOSS_P2_SKILL_CSV_TEMPLATE_ROW.join(',')].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'boss_phase2_skill_template.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ---- 批量缩略图上传类型 ----
interface ThumbUploadItem {
  file: File; filename: string; bossCode: string | null; bossName: string | null; bossId: number | null;
  status: 'pending' | 'uploading' | 'done' | 'error' | 'unmatched'; url?: string; error?: string;
}

// ---- 英雄批量图片上传类型 ----
interface HeroImgUploadItem {
  file: File; filename: string; heroCode: string | null; heroName: string | null; heroId: number | null;
  level?: number; // 进化阶段用
  status: 'pending' | 'uploading' | 'done' | 'error' | 'unmatched'; url?: string; error?: string;
}

const BossTab: React.FC = () => {
  const [data, setData] = useState<Boss[]>([]);
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [allItems, setAllItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [skillModalBoss, setSkillModalBoss] = useState<Boss | null>(null);
  const [skillSelection, setSkillSelection] = useState<number[]>([]);
  const [lootBoss, setLootBoss] = useState<Boss | null>(null);
  const [lootLoading, setLootLoading] = useState(false);
  const [editing, setEditing] = useState<Boss | null>(null);
  const [form] = Form.useForm();
  const [lootForm] = Form.useForm();
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importDone, setImportDone] = useState<{ ok: number; fail: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [instanceBoss, setInstanceBoss] = useState<Boss | null>(null);
  const [instances, setInstances] = useState<any[]>([]);
  const [instancesLoading, setInstancesLoading] = useState(false);
  const [generateCount, setGenerateCount] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [batchGenOpen, setBatchGenOpen] = useState(false);
  const [batchGenCounts, setBatchGenCounts] = useState<Record<string, number>>({ N: 1, R: 1, SR: 1, SSR: 1, UR: 1 });
  const [batchGenLoading, setBatchGenLoading] = useState(false);
  const [batchGenProgress, setBatchGenProgress] = useState(0);
  const [batchGenLog, setBatchGenLog] = useState<string[]>([]);
  const [phase2Boss, setPhase2Boss] = useState<Boss | null>(null);
  const [phase2Data, setPhase2Data] = useState<BossPhase2 | null>(null);
  const [phase2Loading, setPhase2Loading] = useState(false);
  const [phase2SkillSelection, setPhase2SkillSelection] = useState<number[]>([]);
  const [phase2Form] = Form.useForm();
  const [lootImportModalOpen, setLootImportModalOpen] = useState(false);
  const [lootImportRows, setLootImportRows] = useState<LootImportRow[]>([]);
  const [lootImportLoading, setLootImportLoading] = useState(false);
  const [lootImportProgress, setLootImportProgress] = useState(0);
  const [lootImportDone, setLootImportDone] = useState<{ ok: number; fail: number } | null>(null);
  const lootFileInputRef = useRef<HTMLInputElement>(null);

  // Phase 2 import state
  const [phase2ImportOpen, setPhase2ImportOpen] = useState(false);
  const [phase2ImportRows, setPhase2ImportRows] = useState<Phase2ImportRow[]>([]);
  const [phase2ImportLoading, setPhase2ImportLoading] = useState(false);
  const [phase2ImportProgress, setPhase2ImportProgress] = useState(0);
  const [phase2ImportDone, setPhase2ImportDone] = useState<{ ok: number; fail: number } | null>(null);
  const phase2FileInputRef = useRef<HTMLInputElement>(null);

  // Bulk thumbnail upload state
  const [thumbUploadOpen, setThumbUploadOpen] = useState(false);
  const [thumbItems, setThumbItems] = useState<ThumbUploadItem[]>([]);
  const [thumbUploading, setThumbUploading] = useState(false);
  const thumbMultiInputRef = useRef<HTMLInputElement>(null);

  // Bulk phase2 thumbnail upload state
  const [phase2ThumbUploadOpen, setPhase2ThumbUploadOpen] = useState(false);
  const [phase2ThumbItems, setPhase2ThumbItems] = useState<ThumbUploadItem[]>([]);
  const [phase2ThumbUploading, setPhase2ThumbUploading] = useState(false);
  const phase2ThumbMultiInputRef = useRef<HTMLInputElement>(null);

  // Boss skill bulk import state
  const [bossSkillImportOpen, setBossSkillImportOpen] = useState(false);
  const [bossSkillImportRows, setBossSkillImportRows] = useState<BossSkillImportRow[]>([]);
  const [bossSkillImportLoading, setBossSkillImportLoading] = useState(false);
  const [bossSkillImportProgress, setBossSkillImportProgress] = useState(0);
  const [bossSkillImportDone, setBossSkillImportDone] = useState<{ ok: number; fail: number } | null>(null);
  const bossSkillFileInputRef = useRef<HTMLInputElement>(null);

  // Boss phase2 skill bulk import state
  const [bossP2SkillImportOpen, setBossP2SkillImportOpen] = useState(false);
  const [bossP2SkillImportRows, setBossP2SkillImportRows] = useState<BossP2SkillImportRow[]>([]);
  const [bossP2SkillImportLoading, setBossP2SkillImportLoading] = useState(false);
  const [bossP2SkillImportProgress, setBossP2SkillImportProgress] = useState(0);
  const [bossP2SkillImportDone, setBossP2SkillImportDone] = useState<{ ok: number; fail: number } | null>(null);
  const bossP2SkillFileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, sRes, iRes] = await Promise.all([
        fetch(`${BASE_URL}/bosses`).then(r => r.json()),
        fetch(`${BASE_URL}/skills`).then(r => r.json()),
        fetch(`${BASE_URL}/items`).then(r => r.json()),
      ]);
      if (bRes.success) setData(bRes.data);
      if (sRes.success) setAllSkills(sRes.data);
      if (iRes.success) setAllItems(iRes.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openLootModal = async (r: Boss) => {
    setLootBoss(r);
    setLootLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/bosses/${r.id}/loot`);
      const json = await res.json();
      lootForm.setFieldsValue({ loot: json.success ? json.data.map((l: BossLoot) => ({ itemId: l.itemId, dropChance: l.dropChance, minQuantity: l.minQuantity, maxQuantity: l.maxQuantity })) : [] });
    } finally { setLootLoading(false); }
  };

  const handleSaveLoot = async () => {
    if (!lootBoss) return;
    try {
      const values = await lootForm.validateFields();
      const res = await fetch(`${BASE_URL}/bosses/${lootBoss.id}/loot`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ loot: values.loot ?? [] }),
      });
      const json = await res.json();
      if (json.success) { message.success('战利品配置已保存'); setLootBoss(null); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  const openLootImport = () => { setLootImportRows([]); setLootImportDone(null); setLootImportProgress(0); setLootImportModalOpen(true); };

  const handleLootFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { setLootImportRows(parseLootCSV(ev.target?.result as string)); setLootImportDone(null); };
    reader.readAsText(file, 'utf-8'); e.target.value = '';
  };

  const handleLootImport = async () => {
    const validRows = lootImportRows.filter(r => !r._error);
    if (!validRows.length) { message.warning('没有可导入的有效数据'); return; }
    // group by bossCode
    const grouped = new Map<string, LootImportRow[]>();
    for (const row of validRows) {
      const list = grouped.get(row.bossCode) ?? [];
      list.push(row); grouped.set(row.bossCode, list);
    }
    setLootImportLoading(true); setLootImportProgress(0);
    let ok = 0; let fail = 0; const total = grouped.size; let done = 0;
    const groupedEntries = Array.from(grouped.entries());
    for (const [bossCode, rows] of groupedEntries) {
      const boss = data.find(b => b.code === bossCode);
      if (!boss) { fail += rows.length; done++; setLootImportProgress(Math.round((done / total) * 100)); continue; }
      const loot = rows.map((r: LootImportRow) => {
        const item = allItems.find(i => i.name.trim() === r.itemName.trim());
        return item ? { itemId: item.id, dropChance: r.dropChance, minQuantity: r.minQuantity, maxQuantity: r.maxQuantity } : null;
      }).filter(Boolean);
      try {
        const res = await fetch(`${BASE_URL}/bosses/${boss.id}/loot`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ loot }) });
        const json = await res.json();
        if (json.success) ok += rows.length; else fail += rows.length;
      } catch { fail += rows.length; }
      done++; setLootImportProgress(Math.round((done / total) * 100));
    }
    setLootImportLoading(false); setLootImportDone({ ok, fail });
    if (ok > 0) load();
  };

  const openImport = () => {
    setImportRows([]);
    setImportDone(null);
    setImportProgress(0);
    setImportModalOpen(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const rows = parseCSV(text);
      setImportRows(rows);
      setImportDone(null);
    };
    reader.readAsText(file, 'utf-8');
    e.target.value = '';
  };

  const handleImport = async () => {
    const validRows = importRows.filter(r => !r._error);
    if (!validRows.length) { message.warning('没有可导入的有效数据'); return; }
    setImportLoading(true);
    setImportProgress(0);
    let ok = 0; let fail = 0;
    for (let i = 0; i < validRows.length; i++) {
      const { _line, _error, ...body } = validRows[i] as any;
      try {
        const res = await fetch(`${BASE_URL}/bosses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const json = await res.json();
        if (json.success) ok++; else fail++;
      } catch { fail++; }
      setImportProgress(Math.round(((i + 1) / validRows.length) * 100));
    }
    setImportLoading(false);
    setImportDone({ ok, fail });
    if (ok > 0) load();
  };

  const openInstances = async (r: Boss) => {
    setInstanceBoss(r);
    setInstances([]);
    setGenerateCount(1);
    setInstancesLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/bosses/${r.id}/instances`);
      const json = await res.json();
      if (json.success) setInstances(json.data);
    } finally { setInstancesLoading(false); }
  };

  const handleGenerate = async () => {
    if (!instanceBoss) return;
    setGenerating(true);
    try {
      const res = await fetch(`${BASE_URL}/bosses/${instanceBoss.id}/instances`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: generateCount }),
      });
      const json = await res.json();
      if (json.success) {
        message.success(`已生成 ${json.data.length} 个实例`);
        const newInstances = json.data as any[];
        setInstances(prev => [...newInstances, ...prev]);
        // 生成后自动下载新增编码
        if (newInstances.length > 0 && instanceBoss) {
          const content = newInstances.map((inst: any) => `https://ds2.cn/shj/${inst.cardCode}`).join('\r\n') + '\r\n';
          const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
          const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
          saveAs(blob, `${instanceBoss.cardCode || instanceBoss.code}.txt`);
        }
      } else { message.error(json.message); }
    } catch (e: any) { message.error(e.message); }
    finally { setGenerating(false); }
  };

  // 下载当前 Boss 所有卡牌编码为 txt 文件（优先用卡牌编码字段命名）
  const handleDownloadInstances = (targetInstances?: any[]) => {
    const list = targetInstances ?? instances;
    if (!instanceBoss || list.length === 0) { message.warning('暂无卡牌实例可下载'); return; }
    const fileName = `${instanceBoss.cardCode || instanceBoss.code}.txt`;
    const content = list.map((inst: any) => `https://ds2.cn/shj/${inst.cardCode}`).join('\r\n') + '\r\n';
    const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, fileName);
    message.success(`已下载 ${list.length} 条卡牌编码 → ${fileName}`);
  };

  // 批量为所有 Boss 生成实例并各自下载 txt（按稀有度设不同数量）
  const handleBatchGenAll = async () => {
    setBatchGenLoading(true);
    setBatchGenProgress(0);
    setBatchGenLog([]);
    const bossList = data;
    for (let i = 0; i < bossList.length; i++) {
      const boss = bossList[i];
      const count = batchGenCounts[boss.rarity] ?? 1;
      const fileName = `${boss.cardCode || boss.code}.txt`; // 优先用卡牌编码，否则用code
      if (count <= 0) {
        setBatchGenLog(prev => [...prev, `⏭ ${boss.code} (${boss.rarity}) — 数量为0，跳过`]);
        setBatchGenProgress(Math.round(((i + 1) / bossList.length) * 100));
        continue;
      }
      try {
        const res = await fetch(`${BASE_URL}/bosses/${boss.id}/instances`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ count }),
        });
        const json = await res.json();
        if (json.success && json.data?.length) {
          const content = json.data.map((inst: any) => `https://ds2.cn/shj/${inst.cardCode}`).join('\r\n') + '\r\n';
          const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
          const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
          saveAs(blob, fileName);
          setBatchGenLog(prev => [...prev, `✅ ${boss.code} [${boss.rarity}] → ${fileName} (${json.data.length} 张)`]);
        } else {
          setBatchGenLog(prev => [...prev, `❌ ${boss.code} — ${json.message ?? '失败'}`]);
        }
      } catch {
        setBatchGenLog(prev => [...prev, `❌ ${boss.code} — 网络错误`]);
      }
      setBatchGenProgress(Math.round(((i + 1) / bossList.length) * 100));
      await new Promise(r => setTimeout(r, 300));
    }
    setBatchGenLoading(false);
    message.success('批量生成完成');
  };

  const openPhase2 = async (r: Boss) => {
    setPhase2Boss(r);
    setPhase2Loading(true);
    setPhase2Data(null);
    phase2Form.resetFields();
    try {
      const res = await fetch(`${BASE_URL}/bosses/${r.id}/phase2`).then(x => x.json());
      if (res.success && res.data) {
        setPhase2Data(res.data);
        setPhase2SkillSelection((res.data.skills ?? []).map((s: Skill) => s.id));
        phase2Form.setFieldsValue({ ...res.data, attributes: res.data.attributes ?? [] });
      } else {
        setPhase2SkillSelection([]);
        phase2Form.setFieldsValue({ hp: 120, atk: 18, def: 8, speed: 12, attributes: [] });
      }
    } finally { setPhase2Loading(false); }
  };

  const handleSavePhase2 = async () => {
    if (!phase2Boss) return;
    try {
      const values = await phase2Form.validateFields();
      const res = await fetch(`${BASE_URL}/bosses/${phase2Boss.id}/phase2`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      }).then(x => x.json());
      if (!res.success) { message.error(res.message); return; }

      await fetch(`${BASE_URL}/bosses/${phase2Boss.id}/phase2/skills`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skillIds: phase2SkillSelection }),
      });

      message.success('二阶段配置已保存');
      setPhase2Boss(null);
      load();
    } catch (e: any) { message.error(e.message); }
  };

  const handleDeletePhase2 = async () => {
    if (!phase2Boss) return;
    await fetch(`${BASE_URL}/bosses/${phase2Boss.id}/phase2`, { method: 'DELETE' });
    message.success('已删除二阶段配置');
    setPhase2Boss(null);
    load();
  };

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (r: Boss) => {
    setEditing(r);
    form.setFieldsValue({ ...r, attributes: r.attributes ?? [], cooldownHours: r.cooldownSeconds / 3600 });
    setModalOpen(true);
  };
  const openSkillModal = (r: Boss) => {
    setSkillModalBoss(r);
    setSkillSelection((r.skills ?? []).map(s => s.id));
  };

  const handleSubmit = async (values: any) => {
    try {
      const { cooldownHours, ...rest } = values;
      const payload = { ...rest, cooldownSeconds: Math.round((cooldownHours ?? 1) * 3600) };
      const url = editing ? `${BASE_URL}/bosses/${editing.id}` : `${BASE_URL}/bosses`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const json = await res.json();
      if (json.success) { message.success('保存成功'); setModalOpen(false); load(); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  const handleDelete = async (id: number) => {
    const res = await fetch(`${BASE_URL}/bosses/${id}`, { method: 'DELETE' });
    const json = await res.json();
    if (json.success) { message.success('删除成功'); load(); }
    else message.error(json.message);
  };

  const handleSaveSkills = async () => {
    if (!skillModalBoss) return;
    try {
      const res = await fetch(`${BASE_URL}/bosses/${skillModalBoss.id}/skills`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skillIds: skillSelection }),
      });
      const json = await res.json();
      if (json.success) { message.success('技能挂载已保存'); setSkillModalBoss(null); load(); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  // ---- Boss技能批量导入 ----
  const openBossSkillImport = () => { setBossSkillImportRows([]); setBossSkillImportDone(null); setBossSkillImportProgress(0); setBossSkillImportOpen(true); };
  const handleBossSkillFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { setBossSkillImportRows(parseBossSkillCSV(ev.target?.result as string)); setBossSkillImportDone(null); };
    reader.readAsText(file, 'utf-8'); e.target.value = '';
  };
  const handleBossSkillImport = async () => {
    const valid = bossSkillImportRows.filter(r => !r._error);
    if (!valid.length) { message.warning('没有可导入的有效数据'); return; }
    setBossSkillImportLoading(true); setBossSkillImportProgress(0);
    let ok = 0; let fail = 0;
    for (let i = 0; i < valid.length; i++) {
      const r = valid[i];
      const boss = data.find(b => b.code === r.bossCode);
      if (!boss) { fail++; setBossSkillImportProgress(Math.round(((i + 1) / valid.length) * 100)); continue; }
      const skillIds = r.skillNames
        .map(name => allSkills.find(s => s.name.trim() === name.trim())?.id)
        .filter((id): id is number => id !== undefined);
      try {
        const res = await fetch(`${BASE_URL}/bosses/${boss.id}/skills`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ skillIds }),
        });
        const json = await res.json();
        if (json.success) ok++; else fail++;
      } catch { fail++; }
      setBossSkillImportProgress(Math.round(((i + 1) / valid.length) * 100));
    }
    setBossSkillImportLoading(false); setBossSkillImportDone({ ok, fail });
    if (ok > 0) load();
  };

  // ---- Boss二阶段技能批量导入 ----
  const openBossP2SkillImport = () => { setBossP2SkillImportRows([]); setBossP2SkillImportDone(null); setBossP2SkillImportProgress(0); setBossP2SkillImportOpen(true); };
  const handleBossP2SkillFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { setBossP2SkillImportRows(parseBossP2SkillCSV(ev.target?.result as string)); setBossP2SkillImportDone(null); };
    reader.readAsText(file, 'utf-8'); e.target.value = '';
  };
  const handleBossP2SkillImport = async () => {
    const valid = bossP2SkillImportRows.filter(r => !r._error);
    if (!valid.length) { message.warning('没有可导入的有效数据'); return; }
    setBossP2SkillImportLoading(true); setBossP2SkillImportProgress(0);
    let ok = 0; let fail = 0;
    for (let i = 0; i < valid.length; i++) {
      const r = valid[i];
      const boss = data.find(b => b.code === r.bossCode);
      if (!boss) { fail++; setBossP2SkillImportProgress(Math.round(((i + 1) / valid.length) * 100)); continue; }
      const skillIds = r.skillNames
        .map(name => allSkills.find(s => s.name.trim() === name.trim())?.id)
        .filter((id): id is number => id !== undefined);
      try {
        const res = await fetch(`${BASE_URL}/bosses/${boss.id}/phase2/skills`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ skillIds }),
        });
        const json = await res.json();
        if (json.success) ok++; else fail++;
      } catch { fail++; }
      setBossP2SkillImportProgress(Math.round(((i + 1) / valid.length) * 100));
    }
    setBossP2SkillImportLoading(false); setBossP2SkillImportDone({ ok, fail });
    if (ok > 0) load();
  };

  // ---- Phase 2 Import ----
  const openPhase2Import = () => { setPhase2ImportRows([]); setPhase2ImportDone(null); setPhase2ImportProgress(0); setPhase2ImportOpen(true); };
  const handlePhase2FileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { setPhase2ImportRows(parsePhase2CSV(ev.target?.result as string)); setPhase2ImportDone(null); };
    reader.readAsText(file, 'utf-8'); e.target.value = '';
  };
  const handlePhase2Import = async () => {
    const valid = phase2ImportRows.filter(r => !r._error);
    if (!valid.length) { message.warning('没有可导入的有效数据'); return; }
    setPhase2ImportLoading(true); setPhase2ImportProgress(0);
    let ok = 0; let fail = 0; let firstError = '';
    for (let i = 0; i < valid.length; i++) {
      const r = valid[i];
      const boss = data.find(b => b.code === r.bossCode);
      if (!boss) { fail++; if (!firstError) firstError = `未找到Boss编码：${r.bossCode}`; setPhase2ImportProgress(Math.round(((i + 1) / valid.length) * 100)); continue; }
      try {
        // 1. 保存二阶段基础属性
        const r1 = await fetch(`${BASE_URL}/bosses/${boss.id}/phase2`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hp: r.hp, atk: r.atk, def: r.def, speed: r.speed, attributes: r.attributes, description: r.description }),
        });
        const j1 = await r1.json();
        if (!j1.success) { fail++; if (!firstError) firstError = j1.message; setPhase2ImportProgress(Math.round(((i + 1) / valid.length) * 100)); continue; }
        // 2. 解析技能名 → ID，保存技能
        const skillIds = r.skills.map(name => allSkills.find(s => s.name.trim() === name)?.id).filter((id): id is number => !!id);
        await fetch(`${BASE_URL}/bosses/${boss.id}/phase2/skills`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ skillIds }),
        });
        ok++;
      } catch (e: any) { fail++; if (!firstError) firstError = e.message; }
      setPhase2ImportProgress(Math.round(((i + 1) / valid.length) * 100));
    }
    setPhase2ImportLoading(false); setPhase2ImportDone({ ok, fail });
    if (fail > 0 && firstError) message.error(`失败原因：${firstError}`, 6);
  };

  // ---- Bulk Thumbnail Upload ----
  const openThumbUpload = () => { setThumbItems([]); setThumbUploadOpen(true); };
  const handleThumbFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const items: ThumbUploadItem[] = files.map(file => {
      const base = file.name.replace(/\.[^.]+$/, '').trim();
      // Try exact code match first, then name match
      const byCode = data.find(b => b.code.toLowerCase() === base.toLowerCase());
      const byName = !byCode ? data.find(b => b.name === base || b.nameEn === base) : null;
      const matched = byCode ?? byName ?? null;
      return {
        file, filename: file.name,
        bossCode: matched?.code ?? null, bossName: matched?.name ?? null, bossId: matched?.id ?? null,
        status: matched ? 'pending' : 'unmatched',
      };
    });
    setThumbItems(items);
    e.target.value = '';
  };
  const handleThumbUpload = async () => {
    const toUpload = thumbItems.filter(t => t.status === 'pending');
    if (!toUpload.length) { message.warning('没有已匹配的图片'); return; }
    setThumbUploading(true);
    const updated = [...thumbItems];
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status !== 'pending') continue;
      updated[i] = { ...updated[i], status: 'uploading' };
      setThumbItems([...updated]);
      try {
        const formData = new FormData();
        formData.append('file', updated[i].file);
        formData.append('name', updated[i].bossCode!);
        const r1 = await fetch(`${BASE_URL}/bosses/upload-avatar`, { method: 'POST', body: formData });
        const j1 = await r1.json();
        if (!j1.success) throw new Error(j1.message);
        const avatarUrl = j1.data.url;
        // Update boss avatarUrl
        const r2 = await fetch(`${BASE_URL}/bosses/${updated[i].bossId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ avatarUrl }),
        });
        const j2 = await r2.json();
        if (!j2.success) throw new Error(j2.message);
        updated[i] = { ...updated[i], status: 'done', url: avatarUrl };
      } catch (e: any) {
        updated[i] = { ...updated[i], status: 'error', error: e.message };
      }
      setThumbItems([...updated]);
    }
    setThumbUploading(false);
    load();
  };

  // ---- Bulk Phase2 Thumbnail Upload ----
  const openPhase2ThumbUpload = () => { setPhase2ThumbItems([]); setPhase2ThumbUploadOpen(true); };
  const handlePhase2ThumbFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const items: ThumbUploadItem[] = files.map(file => {
      const base = file.name.replace(/\.[^.]+$/, '').trim();
      const boss = data.find(b => b.code === base) ?? data.find(b => b.name === base);
      return {
        file, filename: file.name,
        bossCode: boss?.code ?? null, bossName: boss?.name ?? null, bossId: boss?.id ?? null,
        status: boss ? 'pending' : 'unmatched',
      };
    });
    setPhase2ThumbItems(items);
    e.target.value = '';
  };
  const handlePhase2ThumbUpload = async () => {
    const toUpload = phase2ThumbItems.filter(t => t.status === 'pending');
    if (!toUpload.length) { message.warning('没有已匹配的图片'); return; }
    setPhase2ThumbUploading(true);
    const updated = [...phase2ThumbItems];
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status !== 'pending') continue;
      try {
        const formData = new FormData();
        formData.append('file', updated[i].file);
        formData.append('name', `p2-${updated[i].bossCode}`);
        const r1 = await fetch(`${BASE_URL}/bosses/upload-avatar`, { method: 'POST', body: formData });
        const j1 = await r1.json();
        if (!j1.success) throw new Error(j1.message);
        const avatarUrl = j1.data.url;
        // Update phase2 avatarUrl via PUT /bosses/:id/phase2
        const r2 = await fetch(`${BASE_URL}/bosses/${updated[i].bossId}/phase2`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ avatarUrl }),
        });
        const j2 = await r2.json();
        if (!j2.success) throw new Error(j2.message);
        updated[i] = { ...updated[i], status: 'done', url: avatarUrl };
      } catch (e: any) {
        updated[i] = { ...updated[i], status: 'error', error: e.message };
      }
      setPhase2ThumbItems([...updated]);
    }
    setPhase2ThumbUploading(false);
    load();
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 50 },
    {
      title: '图片', dataIndex: 'avatarUrl', width: 60,
      render: (v: string) => v ? <img src={ossThumb(v, 200)} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }} /> : <span style={{ color: '#ccc' }}>—</span>,
    },
    { title: '编码', dataIndex: 'code', width: 90 },
    {
      title: '卡牌编码', dataIndex: 'cardCode', width: 100,
      render: (v: string, r: Boss) => v
        ? <code style={{ fontSize: 12 }}>{v}</code>
        : <span style={{ color: '#bbb', fontSize: 12 }}>{r.code}</span>,
    },
    {
      title: '名称', key: 'name', width: 160,
      render: (_: any, r: Boss) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 600 }}>{r.name}</span>
          {r.nameEn && <span style={{ fontSize: 11, color: '#888' }}>{r.nameEn}</span>}
        </Space>
      ),
    },
    { title: '稀有度', dataIndex: 'rarity', width: 70, render: (v: string) => <Tag color={RARITY_COLOR[v] ?? '#8c8c8c'}>{v}</Tag> },
    { title: '难度', dataIndex: 'difficulty', width: 100, render: (v: number) => <span style={{ color: '#faad14' }}>{DIFFICULTY_LABEL[v]}</span> },
    { title: 'HP', dataIndex: 'hp', width: 55 },
    { title: 'ATK', dataIndex: 'atk', width: 55 },
    { title: 'DEF', dataIndex: 'def', width: 55 },
    { title: '经验', dataIndex: 'expReward', width: 60 },
    { title: 'SPD', dataIndex: 'speed', width: 50 },
    {
      title: '冷却', dataIndex: 'cooldownSeconds', width: 70,
      render: (v: number) => `${+(v / 3600).toFixed(1)}h`,
    },
    {
      title: '属性', dataIndex: 'attributes', width: 120,
      render: (attrs: string[]) => (attrs ?? []).map(a => <Tag key={a} style={{ margin: '0 2px 2px 0' }}>{a}</Tag>),
    },
    {
      title: '技能', key: 'skills', width: 140,
      render: (_: any, r: Boss) => (r.skills ?? []).length > 0
        ? (r.skills ?? []).map(s => {
            const [color, icon] = s.skillType === 'damage' ? ['red', '⚔'] : s.skillType === 'status' ? ['purple', '✨'] : s.skillType === 'heal' ? ['green', '💚'] : ['orange', '⚔✨'];
            return <Tag key={s.id} color={color} style={{ margin: '0 2px 2px 0' }}>{icon} {s.name}</Tag>;
          })
        : <span style={{ color: '#999', fontSize: 12 }}>—</span>,
    },
    {
      title: '状态', dataIndex: 'isActive', width: 70,
      render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '二阶段', dataIndex: 'phase2Chance', width: 70,
      render: (v: number) => v > 0
        ? <Tag color="purple">{v}%</Tag>
        : <span style={{ color: '#999', fontSize: 12 }}>—</span>,
    },
    {
      title: '操作', width: 240,
      render: (_: any, r: Boss) => (
        <Space>
          <Button size="small" icon={<ThunderboltOutlined />} onClick={() => openInstances(r)}>实例</Button>
          <Button size="small" onClick={() => openSkillModal(r)}>技能</Button>
          <Button size="small" onClick={() => openLootModal(r)}>战利品</Button>
          {(r.rarity === 'SSR' || r.rarity === 'UR')
            ? r.hasPhase2
              ? <Button size="small" style={{ color: '#fff', background: '#a855f7', borderColor: '#a855f7' }} onClick={() => openPhase2(r)}>二阶段 ✓</Button>
              : <Button size="small" style={{ color: '#a855f7', borderColor: '#a855f7' }} onClick={() => openPhase2(r)}>二阶段</Button>
            : <Button size="small" disabled>二阶段</Button>
          }
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Boss管理"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
          <Button icon={<DownloadOutlined />} onClick={() => { setBatchGenOpen(true); setBatchGenLog([]); setBatchGenProgress(0); }}>批量生成卡牌编码</Button>
          <Button icon={<UploadOutlined />} onClick={openImport}>Boss导入</Button>
          <Button icon={<UploadOutlined />} onClick={openLootImport}>掉落导入</Button>
          <Button icon={<UploadOutlined />} onClick={openPhase2Import}>二阶段导入</Button>
          <Button icon={<UploadOutlined />} onClick={openThumbUpload}>批量缩略图</Button>
          <Button icon={<UploadOutlined />} onClick={openPhase2ThumbUpload}>二阶段批量缩略图</Button>
          <Button icon={<UploadOutlined />} onClick={openBossSkillImport}>批量技能</Button>
          <Button icon={<UploadOutlined />} onClick={openBossP2SkillImport}>二阶段批量技能</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增Boss</Button>
        </Space>
      }
    >
      <input ref={lootFileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleLootFileChange} />
      <input ref={phase2FileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handlePhase2FileChange} />
      <input ref={bossSkillFileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleBossSkillFileChange} />
      <input ref={bossP2SkillFileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleBossP2SkillFileChange} />
      <input ref={thumbMultiInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleThumbFilesChange} />
      <input ref={phase2ThumbMultiInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handlePhase2ThumbFilesChange} />
      <Table
        rowKey="id"
        dataSource={data}
        columns={columns}
        loading={loading}
        size="small"
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1100 }}
        expandable={{
          rowExpandable: r => !!r.hasPhase2,
          defaultExpandAllRows: true,
          expandedRowRender: (r: Boss) => {
            const p2 = r.phase2Data!;
            const diff = (a: number, b: number) => {
              const d = a - b;
              if (d === 0) return <span style={{ color: '#999' }}>—</span>;
              return <span style={{ color: d > 0 ? '#f5222d' : '#52c41a', fontSize: 11 }}>{d > 0 ? `+${d}` : d}</span>;
            };
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 24, padding: '4px 8px 4px 48px', background: '#f9f0ff', borderRadius: 6 }}>
                {p2.avatarUrl && <img src={ossThumb(p2.avatarUrl, 200)} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, border: '1px solid #d3adf7' }} />}
                <Tag color="purple" style={{ fontSize: 11 }}>二阶段</Tag>
                {[
                  { label: 'HP', val: p2.hp, base: r.hp },
                  { label: 'ATK', val: p2.atk, base: r.atk },
                  { label: 'DEF', val: p2.def, base: r.def },
                  { label: 'SPD', val: p2.speed, base: r.speed },
                ].map(({ label, val, base }) => (
                  <Space key={label} size={3}>
                    <span style={{ color: '#888', fontSize: 11 }}>{label}</span>
                    <span style={{ fontWeight: 600 }}>{val}</span>
                    {diff(val, base)}
                  </Space>
                ))}
                {(p2.attributes ?? []).length > 0 && (
                  <Space size={2}>
                    {(p2.attributes ?? []).map(a => <Tag key={a} color="purple" style={{ fontSize: 11, margin: 0 }}>{a}</Tag>)}
                  </Space>
                )}
                {(p2.skills ?? []).length > 0 && (
                  <Space size={2} wrap>
                    {(p2.skills ?? []).map(s => <Tag key={s.id} color="volcano" style={{ fontSize: 11, margin: 0 }}>{s.name}</Tag>)}
                  </Space>
                )}
              </div>
            );
          },
        }}
      />

      {/* Boss 编辑 Modal */}
      <Modal title={editing ? '编辑Boss' : '新增Boss'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={() => form.submit()} okText="保存" width={600}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={12}>
            <Col span={6}><Form.Item name="code" label="编码" rules={[{ required: true }]}><Input placeholder="SHJ_N_001" /></Form.Item></Col>
            <Col span={6}>
              <Form.Item name="cardCode" label="卡牌编码" tooltip="用于批量生成文件命名，留空则与「编码」相同">
                <Input placeholder="留空同编码" />
              </Form.Item>
            </Col>
            <Col span={6}><Form.Item name="name" label="中文名" rules={[{ required: true }]}><Input placeholder="都市幽灵" /></Form.Item></Col>
            <Col span={6}><Form.Item name="nameEn" label="英文名"><Input placeholder="Urban Ghost" /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="rarity" label="稀有度" initialValue="N">
                <Input placeholder="N / R / SR / UR 或自定义" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="difficulty" label="难度(1-5)" initialValue={1}>
                <Select>{[1,2,3,4,5].map(v => <Select.Option key={v} value={v}>{DIFFICULTY_LABEL[v]}</Select.Option>)}</Select>
              </Form.Item>
            </Col>
            <Col span={8}><Form.Item name="expReward" label="胜利经验" initialValue={50}><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}><Form.Item name="hp" label="HP" initialValue={80}><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
            <Col span={6}><Form.Item name="atk" label="ATK" initialValue={12}><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
            <Col span={6}><Form.Item name="def" label="DEF" initialValue={5}><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
            <Col span={6}><Form.Item name="speed" label="SPD" initialValue={10}><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="cooldownHours" label="复活冷却（小时）" initialValue={1}>
                <InputNumber style={{ width: '100%' }} min={0} step={0.5} addonAfter="小时" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="phase2Chance" label="二阶段触发概率" initialValue={0}>
                <InputNumber style={{ width: '100%' }} min={0} max={100} addonAfter="%" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="attributes" label="属性标签（可多选）">
                <Select mode="tags" placeholder="选择或输入属性" tokenSeparators={[',']}>
                  {ATTR_PRESETS.map(a => <Select.Option key={a} value={a}>{a}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="描述"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="avatarUrl" label="头像">
            <Form.Item shouldUpdate={(prev, cur) => prev.avatarUrl !== cur.avatarUrl || prev.code !== cur.code} noStyle>
              {({ getFieldValue, setFieldValue }) => (
                <AvatarUpload
                  uploadUrl={`${BASE_URL}/bosses/upload-avatar`}
                  nameField={getFieldValue('code') || `boss-${Date.now()}`}
                  value={getFieldValue('avatarUrl')}
                  onChange={url => setFieldValue('avatarUrl', url)}
                />
              )}
            </Form.Item>
          </Form.Item>
          <Form.Item name="isActive" label="状态" initialValue={true}>
            <Select><Select.Option value={true}>启用</Select.Option><Select.Option value={false}>禁用</Select.Option></Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 二阶段配置 Modal */}
      <Modal
        title={phase2Boss ? `二阶段配置 — ${phase2Boss.name}` : ''}
        open={!!phase2Boss}
        onCancel={() => setPhase2Boss(null)}
        onOk={handleSavePhase2}
        okText="保存"
        width={600}
        footer={[
          phase2Data && (
            <Popconfirm key="del" title="确认删除二阶段配置？" onConfirm={handleDeletePhase2}>
              <Button danger>删除二阶段</Button>
            </Popconfirm>
          ),
          <Button key="cancel" onClick={() => setPhase2Boss(null)}>取消</Button>,
          <Button key="ok" type="primary" onClick={handleSavePhase2}>保存</Button>,
        ]}
      >
        {phase2Loading ? <div style={{ textAlign: 'center', padding: 32 }}>加载中...</div> : (
          <>
            <Alert
              type="info"
              showIcon
              message="二阶段触发概率在 Boss 编辑表单的「二阶段触发概率」字段设置（0=无二阶段）"
              style={{ marginBottom: 16 }}
            />
            <Form form={phase2Form} layout="vertical">
              <Row gutter={12}>
                <Col span={6}><Form.Item name="hp" label="HP" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
                <Col span={6}><Form.Item name="atk" label="ATK" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
                <Col span={6}><Form.Item name="def" label="DEF" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
                <Col span={6}><Form.Item name="speed" label="SPD" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={1} /></Form.Item></Col>
              </Row>
              <Form.Item name="attributes" label="属性标签">
                <Select mode="tags" placeholder="选择或输入属性" tokenSeparators={[',']}>
                  {ATTR_PRESETS.map(a => <Select.Option key={a} value={a}>{a}</Select.Option>)}
                </Select>
              </Form.Item>
              <Form.Item name="description" label="描述（选填）"><Input.TextArea rows={2} /></Form.Item>
              <Form.Item name="avatarUrl" label="二阶段头像（选填，不填则沿用一阶段）">
                <Form.Item shouldUpdate={(prev, cur) => prev.avatarUrl !== cur.avatarUrl} noStyle>
                  {({ getFieldValue, setFieldValue }) => (
                    <AvatarUpload
                      uploadUrl={`${BASE_URL}/bosses/upload-avatar`}
                      nameField={`p2-${phase2Boss?.id ?? 0}`}
                      value={getFieldValue('avatarUrl')}
                      onChange={url => setFieldValue('avatarUrl', url)}
                    />
                  )}
                </Form.Item>
              </Form.Item>
              <Form.Item label="二阶段技能">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {allSkills.map(s => {
                    const [color] = s.skillType === 'damage' ? ['red'] : s.skillType === 'status' ? ['purple'] : s.skillType === 'heal' ? ['green'] : ['orange'];
                    const selected = phase2SkillSelection.includes(s.id);
                    return (
                      <Tag
                        key={s.id}
                        color={selected ? color : undefined}
                        style={{ cursor: 'pointer', opacity: selected ? 1 : 0.45 }}
                        onClick={() => setPhase2SkillSelection(prev => selected ? prev.filter(id => id !== s.id) : [...prev, s.id])}
                      >
                        {s.name}
                      </Tag>
                    );
                  })}
                </div>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* 批量生成卡牌编码 Modal */}
      <Modal
        title="批量生成卡牌编码"
        open={batchGenOpen}
        onCancel={() => { if (!batchGenLoading) setBatchGenOpen(false); }}
        footer={[
          <Button key="close" onClick={() => setBatchGenOpen(false)} disabled={batchGenLoading}>关闭</Button>,
          <Button key="go" type="primary" icon={<ThunderboltOutlined />} loading={batchGenLoading} onClick={handleBatchGenAll}>
            开始生成并下载
          </Button>,
        ]}
        width={520}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            type="info"
            message={`将为全部 ${data.length} 个 Boss 各自生成卡牌，文件名用「卡牌编码」字段（未填则用「编码」）。数量为 0 则跳过该稀有度。`}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#fafafa', textAlign: 'left' }}>
                <th style={{ padding: '6px 12px', borderBottom: '1px solid #f0f0f0', width: 60 }}>稀有度</th>
                <th style={{ padding: '6px 12px', borderBottom: '1px solid #f0f0f0' }}>Boss 数量</th>
                <th style={{ padding: '6px 12px', borderBottom: '1px solid #f0f0f0', width: 160 }}>每个生成卡牌张数</th>
              </tr>
            </thead>
            <tbody>
              {(['N','R','SR','SSR','UR'] as const).map(rarity => {
                const count = data.filter(b => b.rarity === rarity).length;
                return (
                  <tr key={rarity} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px 12px' }}>
                      <Tag color={RARITY_COLOR[rarity]}>{rarity}</Tag>
                    </td>
                    <td style={{ padding: '8px 12px', color: count === 0 ? '#ccc' : undefined }}>
                      {count === 0 ? '—' : `${count} 个`}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <InputNumber
                        min={0} max={500}
                        value={batchGenCounts[rarity] ?? 1}
                        onChange={v => setBatchGenCounts(prev => ({ ...prev, [rarity]: v ?? 0 }))}
                        style={{ width: 100 }}
                        disabled={count === 0}
                        addonAfter="张"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {batchGenLoading && <Progress percent={batchGenProgress} status="active" />}
          {batchGenLog.length > 0 && (
            <div style={{ maxHeight: 240, overflowY: 'auto', background: '#fafafa', border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px 12px', fontSize: 12, fontFamily: 'monospace' }}>
              {batchGenLog.map((line, i) => <div key={i}>{line}</div>)}
            </div>
          )}
        </Space>
      </Modal>

      {/* 实例管理 Modal */}
      <Modal
        title={instanceBoss ? `实例管理 — ${instanceBoss.name}` : ''}
        open={!!instanceBoss}
        onCancel={() => setInstanceBoss(null)}
        footer={<Button onClick={() => setInstanceBoss(null)}>关闭</Button>}
        width={760}
      >
        <Space style={{ marginBottom: 12 }} wrap>
          <InputNumber min={1} max={200} value={generateCount} onChange={v => setGenerateCount(v ?? 1)} addonBefore="生成数量" style={{ width: 160 }} />
          <Button type="primary" icon={<ThunderboltOutlined />} loading={generating} onClick={handleGenerate}>
            生成并下载
          </Button>
          <Button icon={<DownloadOutlined />} onClick={() => handleDownloadInstances()} disabled={instances.length === 0}>
            下载全部编码（{instances.length} 条）
          </Button>
          <span style={{ color: '#999', fontSize: 12 }}>文件名：{instanceBoss ? (instanceBoss.cardCode || instanceBoss.code) : ''}.txt</span>
        </Space>
        <Table
          rowKey="id"
          size="small"
          loading={instancesLoading}
          pagination={{ pageSize: 10 }}
          dataSource={instances}
          columns={[
            { title: 'ID', dataIndex: 'id', width: 55 },
            {
              title: '卡牌编码', dataIndex: 'cardCode', width: 150,
              render: (v: string) => <code style={{ fontSize: 13, userSelect: 'all' }}>{v}</code>,
            },
            {
              title: 'QR码', dataIndex: 'cardCode', key: 'qr', width: 90,
              render: (v: string) => <QRCodeSVG value={`https://ds2.cn/shj/${v}`} size={64} />,
            },
            {
              title: '状态', dataIndex: 'isActive', width: 70,
              render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? '启用' : '禁用'}</Tag>,
            },
            {
              title: '创建时间', dataIndex: 'created', width: 150,
              render: (v: string) => new Date(v).toLocaleString('zh-CN'),
            },
          ]}
        />
      </Modal>

      {/* 表格导入 Modal */}
      <Modal
        title="批量导入Boss"
        open={importModalOpen}
        onCancel={() => { if (!importLoading) setImportModalOpen(false); }}
        width={900}
        footer={[
          <Button key="tpl" icon={<DownloadOutlined />} onClick={downloadTemplate}>下载模板</Button>,
          <Button key="upload" icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()} disabled={importLoading}>选择CSV文件</Button>,
          <Button key="cancel" onClick={() => setImportModalOpen(false)} disabled={importLoading}>关闭</Button>,
          <Button
            key="import" type="primary" loading={importLoading}
            disabled={importRows.filter(r => !r._error).length === 0 || !!importDone}
            onClick={handleImport}
          >
            导入 {importRows.filter(r => !r._error).length} 条
          </Button>,
        ]}
      >
        <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFileChange} />
        {importRows.length === 0 && !importDone && (
          <Alert
            type="info"
            message="使用说明"
            description={
              <ul style={{ margin: 0, paddingLeft: 16, lineHeight: 2 }}>
                <li>先点「下载模板」获取CSV格式示例</li>
                <li>填写数据后点「选择CSV文件」上传（UTF-8编码）</li>
                <li>属性字段多个值用 <code>|</code> 分隔，如：<code>物理|火</code></li>
                <li>稀有度填 N / R / SR / UR；难度填 1–5</li>
              </ul>
            }
          />
        )}
        {importDone && (
          <Alert
            type={importDone.fail === 0 ? 'success' : 'warning'}
            message={`导入完成：成功 ${importDone.ok} 条，失败 ${importDone.fail} 条`}
            style={{ marginBottom: 12 }}
          />
        )}
        {importLoading && <Progress percent={importProgress} style={{ marginBottom: 12 }} />}
        {importRows.length > 0 && (
          <Table
            rowKey="_line"
            size="small"
            pagination={{ pageSize: 20 }}
            dataSource={importRows}
            rowClassName={(r: ImportRow) => r._error ? 'import-row-error' : ''}
            columns={[
              { title: '行', dataIndex: '_line', width: 45 },
              { title: '编码', dataIndex: 'code', width: 90 },
              { title: '名称', dataIndex: 'name', width: 100 },
              { title: '英文名', dataIndex: 'nameEn', width: 110, ellipsis: true },
              { title: '稀有度', dataIndex: 'rarity', width: 65, render: (v: string) => <Tag color={RARITY_COLOR[v] ?? '#8c8c8c'}>{v}</Tag> },
              { title: '难度', dataIndex: 'difficulty', width: 50 },
              { title: 'HP', dataIndex: 'hp', width: 50 },
              { title: 'ATK', dataIndex: 'atk', width: 50 },
              { title: 'DEF', dataIndex: 'def', width: 50 },
              { title: 'SPD', dataIndex: 'speed', width: 50 },
              { title: '经验', dataIndex: 'expReward', width: 55 },
              {
                title: '属性', dataIndex: 'attributes', width: 100,
                render: (v: string[]) => (v ?? []).map(a => <Tag key={a} style={{ marginBottom: 2 }}>{a}</Tag>),
              },
              {
                title: '状态', key: 'status', width: 90,
                render: (_: any, r: ImportRow) => r._error
                  ? <Tooltip title={r._error}><Tag color="red">错误</Tag></Tooltip>
                  : <Tag color="green">正常</Tag>,
              },
            ]}
          />
        )}
      </Modal>

      {/* 掉落批量导入 Modal */}
      <Modal
        title="批量导入Boss掉落配置（CSV）"
        open={lootImportModalOpen}
        onCancel={() => { if (!lootImportLoading) setLootImportModalOpen(false); }}
        width={820}
        footer={[
          <Button key="tpl" icon={<DownloadOutlined />} onClick={downloadLootTemplate}>下载模板</Button>,
          <Button key="sel" icon={<UploadOutlined />} onClick={() => lootFileInputRef.current?.click()}>选择 CSV 文件</Button>,
          <Button key="go" type="primary" loading={lootImportLoading} disabled={lootImportRows.filter(r => !r._error).length === 0} onClick={handleLootImport}>
            导入 {lootImportRows.filter(r => !r._error).length} 条
          </Button>,
        ]}
      >
        {lootImportLoading && <Progress percent={lootImportProgress} style={{ marginBottom: 12 }} />}
        {lootImportDone && <Alert type={lootImportDone.fail === 0 ? 'success' : 'warning'} message={`导入完成：成功 ${lootImportDone.ok} 条，失败 ${lootImportDone.fail} 条`} style={{ marginBottom: 12 }} />}
        {lootImportRows.length > 0 ? (
          <Table rowKey="_line" size="small" dataSource={lootImportRows} rowClassName={(r: LootImportRow) => r._error ? 'import-row-error' : ''} pagination={{ pageSize: 20 }}
            columns={[
              { title: '行', dataIndex: '_line', width: 45 },
              { title: 'Boss编码', dataIndex: 'bossCode', width: 160, render: (v: string) => { const found = data.find(b => b.code === v); return found ? <><Tag color="blue">{v}</Tag> {found.name}</> : <Tag color="red">{v} ⚠未找到</Tag>; } },
              { title: '道具名', dataIndex: 'itemName', width: 130, render: (v: string) => { const found = allItems.find(i => i.name.trim() === v.trim()); return found ? <Tag color="green">{v}</Tag> : <Tag color="orange">{v} ⚠未匹配</Tag>; } },
              { title: '掉落率', dataIndex: 'dropChance', width: 75, render: (v: number) => `${v}%` },
              { title: '数量', key: 'qty', width: 70, render: (_: any, r: LootImportRow) => `${r.minQuantity}~${r.maxQuantity}` },
              { title: '状态', width: 140, render: (_: any, r: LootImportRow) => r._error ? <span style={{ color: '#ff4d4f', fontSize: 12 }}>{r._error}</span> : <span style={{ color: '#52c41a', fontSize: 12 }}>✓ 正常</span> },
            ]}
          />
        ) : !lootImportDone && (
          <Alert type="info" message="CSV 格式说明"
            description={<div style={{ fontSize: 12 }}>
              <p>列：<code>bossCode, itemName, dropChance, minQuantity, maxQuantity</code></p>
              <p>• <b>bossCode</b>：Boss的编码（如 SHJ_N_001），同一Boss可有多行对应多种掉落</p>
              <p>• <b>itemName</b>：道具名称（需与系统中道具名完全匹配）</p>
              <p>• <b>dropChance</b>：掉落概率 1-100（%），同一Boss多个道具独立判定</p>
              <p>• 同一Boss的所有掉落行会被合并为一次批量保存（覆盖原有配置）</p>
            </div>}
          />
        )}
      </Modal>

      {/* 挂载技能 Modal */}
      <Modal
        title={skillModalBoss ? `挂载技能 — ${skillModalBoss.name}` : ''}
        open={!!skillModalBoss}
        onCancel={() => setSkillModalBoss(null)}
        onOk={handleSaveSkills}
        okText="保存"
      >
        {skillModalBoss && (
          allSkills.length === 0
            ? <span style={{ color: '#999' }}>暂无技能，请先在「技能管理」Tab创建技能</span>
            : <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={allSkills}
                rowSelection={{
                  selectedRowKeys: skillSelection,
                  onChange: keys => setSkillSelection(keys as number[]),
                }}
                columns={[
                  { title: '名称', dataIndex: 'name', render: (v: string, r: Skill) => <Space><span>{v}</span>{r.nameEn && <span style={{ color: '#999', fontSize: 12 }}>{r.nameEn}</span>}</Space> },
                  { title: '属性', dataIndex: 'attribute', width: 80, render: (v: string) => <Tag>{v}</Tag> },
                  { title: '描述', dataIndex: 'description', ellipsis: true },
                ]}
              />
        )}
      </Modal>

      {/* 战利品配置 Modal */}
      <Modal
        title={lootBoss ? `战利品配置 — ${lootBoss.name}` : ''}
        open={!!lootBoss}
        onCancel={() => setLootBoss(null)}
        onOk={handleSaveLoot}
        okText="保存"
        width={680}
        confirmLoading={lootLoading}
      >
        {allItems.length === 0
          ? <Alert type="warning" message="请先在「道具管理」Tab中创建道具" />
          : (
            <Form form={lootForm} layout="vertical">
              <Form.List name="loot">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name }) => (
                      <div key={key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                        <Form.Item name={[name, 'itemId']} label="道具" rules={[{ required: true }]} style={{ flex: 2, marginBottom: 0 }}>
                          <Select placeholder="选择道具" showSearch optionFilterProp="label">
                            {allItems.map(i => (
                              <Select.Option key={i.id} value={i.id}>
                                <Space>
                                  <Tag color={ITEM_TYPE_COLOR[i.type] ?? 'default'}>{ITEM_TYPE_LABEL[i.type] ?? i.type}</Tag>
                                  {i.name}
                                </Space>
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Form.Item name={[name, 'dropChance']} label="掉落率(%)" initialValue={30} rules={[{ required: true }]} style={{ width: 110, marginBottom: 0 }}>
                          <InputNumber min={1} max={100} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name={[name, 'minQuantity']} label="最少数量" initialValue={1} style={{ width: 90, marginBottom: 0 }}>
                          <InputNumber min={1} max={99} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name={[name, 'maxQuantity']} label="最多数量" initialValue={1} style={{ width: 90, marginBottom: 0 }}>
                          <InputNumber min={1} max={99} style={{ width: '100%' }} />
                        </Form.Item>
                        <Button danger size="small" onClick={() => remove(name)} style={{ marginTop: 28 }}>删除</Button>
                      </div>
                    ))}
                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()} block>添加掉落道具</Button>
                  </>
                )}
              </Form.List>
            </Form>
          )}
      </Modal>

      {/* 二阶段批量导入 Modal */}
      <Modal
        title="批量导入Boss二阶段配置（CSV）"
        open={phase2ImportOpen}
        onCancel={() => { if (!phase2ImportLoading) setPhase2ImportOpen(false); }}
        width={860}
        footer={[
          <Button key="tpl" icon={<DownloadOutlined />} onClick={downloadPhase2Template}>下载模板</Button>,
          <Button key="sel" icon={<UploadOutlined />} onClick={() => phase2FileInputRef.current?.click()}>选择CSV文件</Button>,
          <Button key="go" type="primary" loading={phase2ImportLoading}
            disabled={phase2ImportRows.filter(r => !r._error).length === 0 || !!phase2ImportDone}
            onClick={handlePhase2Import}>
            导入 {phase2ImportRows.filter(r => !r._error).length} 条
          </Button>,
          <Button key="close" onClick={() => setPhase2ImportOpen(false)}>关闭</Button>,
        ]}
      >
        {phase2ImportLoading && <Progress percent={phase2ImportProgress} style={{ marginBottom: 12 }} />}
        {phase2ImportDone && <Alert type={phase2ImportDone.fail === 0 ? 'success' : 'warning'} message={`导入完成：成功 ${phase2ImportDone.ok} 条，失败 ${phase2ImportDone.fail} 条`} style={{ marginBottom: 12 }} />}
        {phase2ImportRows.length > 0 ? (
          <Table rowKey="_line" size="small" dataSource={phase2ImportRows} pagination={{ pageSize: 15 }}
            rowClassName={(r: Phase2ImportRow) => r._error ? 'import-row-error' : ''}
            columns={[
              { title: '行', dataIndex: '_line', width: 45 },
              { title: 'Boss编码', dataIndex: 'bossCode', width: 140,
                render: (v: string) => { const found = data.find(b => b.code === v); return found ? <><Tag color="blue">{v}</Tag>{found.name}</> : <Tag color="red">{v} ⚠未找到</Tag>; } },
              { title: 'HP', dataIndex: 'hp', width: 65 },
              { title: 'ATK', dataIndex: 'atk', width: 55 },
              { title: 'DEF', dataIndex: 'def', width: 55 },
              { title: 'SPD', dataIndex: 'speed', width: 55 },
              { title: '属性', dataIndex: 'attributes', width: 90, render: (a: string[]) => a.map(x => <Tag key={x} style={{ margin: '0 2px' }}>{x}</Tag>) },
              { title: '技能（4个）', dataIndex: 'skills', width: 200,
                render: (skills: string[]) => skills.map(s => {
                  const found = allSkills.find(sk => sk.name.trim() === s);
                  return <Tag key={s} color={found ? 'blue' : 'orange'} style={{ margin: '0 2px 2px 0' }}>{s}{!found && ' ⚠'}</Tag>;
                }) },
              { title: '状态', width: 120, render: (_: any, r: Phase2ImportRow) => r._error ? <span style={{ color: '#ff4d4f', fontSize: 12 }}>{r._error}</span> : <span style={{ color: '#52c41a', fontSize: 12 }}>✓ 正常</span> },
            ]}
          />
        ) : !phase2ImportDone && (
          <Alert type="info" message="CSV 格式说明"
            description={<div style={{ fontSize: 12 }}>
              <p>列顺序：<code>bossCode, hp, atk, def, speed, attributes, description, skill1, skill2, skill3, skill4</code></p>
              <p>• <b>attributes</b>：用"|"分隔多个属性（如 混沌|火）</p>
              <p>• <b>skill1~skill4</b>：技能名称，需与技能库完全一致；未匹配的技能会被跳过</p>
              <p>• 导入时若该Boss已有二阶段配置，会覆盖更新</p>
            </div>}
          />
        )}
      </Modal>

      {/* 批量缩略图上传 Modal */}
      <Modal
        title="批量上传Boss缩略图"
        open={thumbUploadOpen}
        onCancel={() => { if (!thumbUploading) setThumbUploadOpen(false); }}
        width={720}
        footer={[
          <Button key="sel" icon={<UploadOutlined />} onClick={() => thumbMultiInputRef.current?.click()} disabled={thumbUploading}>选择图片（可多选）</Button>,
          <Button key="go" type="primary" loading={thumbUploading}
            disabled={thumbItems.filter(t => t.status === 'pending').length === 0}
            onClick={handleThumbUpload}>
            上传 {thumbItems.filter(t => t.status === 'pending').length} 张已匹配图片
          </Button>,
          <Button key="close" onClick={() => setThumbUploadOpen(false)}>关闭</Button>,
        ]}
      >
        <Alert type="info" style={{ marginBottom: 12 }}
          message="命名规则：图片文件名（去除扩展名）需与Boss编码完全一致，例如 SHJ_N_001.png → 匹配 SHJ_N_001。支持 jpg / png / webp。" />
        {thumbItems.length > 0 ? (
          <Table rowKey="filename" size="small" dataSource={thumbItems} pagination={{ pageSize: 15 }}
            columns={[
              { title: '文件名', dataIndex: 'filename', width: 180, ellipsis: true },
              { title: '匹配Boss', key: 'boss', width: 180,
                render: (_: any, r: ThumbUploadItem) => r.bossId
                  ? <Space size={4}><Tag color="blue">{r.bossCode}</Tag><span>{r.bossName}</span></Space>
                  : <Tag color="red">⚠ 未匹配</Tag> },
              { title: '预览', key: 'preview', width: 60,
                render: (_: any, r: ThumbUploadItem) => r.status === 'done' && r.url
                  ? <img src={ossThumb(r.url, 200)} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                  : null },
              { title: '状态', key: 'status', width: 100,
                render: (_: any, r: ThumbUploadItem) => {
                  if (r.status === 'unmatched') return <Tag color="default">未匹配</Tag>;
                  if (r.status === 'pending') return <Tag color="blue">待上传</Tag>;
                  if (r.status === 'uploading') return <Tag color="processing">上传中</Tag>;
                  if (r.status === 'done') return <Tag color="green">✓ 完成</Tag>;
                  if (r.status === 'error') return <Tooltip title={r.error}><Tag color="red">失败</Tag></Tooltip>;
                  return null;
                } },
            ]}
          />
        ) : (
          <div style={{ textAlign: 'center', color: '#999', padding: '40px 0' }}>点击「选择图片」批量选取图片文件</div>
        )}
      </Modal>

      {/* 二阶段批量缩略图上传 Modal */}
      <Modal
        title="批量上传Boss二阶段缩略图"
        open={phase2ThumbUploadOpen}
        onCancel={() => { if (!phase2ThumbUploading) setPhase2ThumbUploadOpen(false); }}
        width={720}
        footer={[
          <Button key="sel" icon={<UploadOutlined />} onClick={() => phase2ThumbMultiInputRef.current?.click()} disabled={phase2ThumbUploading}>选择图片（可多选）</Button>,
          <Button key="go" type="primary" loading={phase2ThumbUploading}
            disabled={phase2ThumbItems.filter(t => t.status === 'pending').length === 0}
            onClick={handlePhase2ThumbUpload}>
            上传 {phase2ThumbItems.filter(t => t.status === 'pending').length} 张已匹配图片
          </Button>,
          <Button key="close" onClick={() => setPhase2ThumbUploadOpen(false)}>关闭</Button>,
        ]}
      >
        <Alert type="info" style={{ marginBottom: 12 }}
          message="命名规则：图片文件名（去除扩展名）需与Boss编码完全一致，例如 SHJ_SSR_001.png → 匹配 SHJ_SSR_001。仅SSR/UR Boss有二阶段。支持 jpg / png / webp。" />
        {phase2ThumbItems.length > 0 ? (
          <Table rowKey="filename" size="small" dataSource={phase2ThumbItems} pagination={{ pageSize: 15 }}
            columns={[
              { title: '文件名', dataIndex: 'filename', width: 180, ellipsis: true },
              { title: '匹配Boss', key: 'boss', width: 180,
                render: (_: any, r: ThumbUploadItem) => r.bossId
                  ? <Space size={4}><Tag color="purple">{r.bossCode}</Tag><span>{r.bossName}</span></Space>
                  : <Tag color="red">⚠ 未匹配</Tag> },
              { title: '预览', key: 'preview', width: 60,
                render: (_: any, r: ThumbUploadItem) => r.status === 'done' && r.url
                  ? <img src={ossThumb(r.url, 200)} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                  : null },
              { title: '状态', key: 'status', width: 100,
                render: (_: any, r: ThumbUploadItem) => {
                  if (r.status === 'unmatched') return <Tag color="default">未匹配</Tag>;
                  if (r.status === 'pending') return <Tag color="blue">待上传</Tag>;
                  if (r.status === 'uploading') return <Tag color="processing">上传中</Tag>;
                  if (r.status === 'done') return <Tag color="green">✓ 完成</Tag>;
                  if (r.status === 'error') return <Tooltip title={r.error}><Tag color="red">失败</Tag></Tooltip>;
                  return null;
                } },
            ]}
          />
        ) : (
          <div style={{ textAlign: 'center', color: '#999', padding: '40px 0' }}>点击「选择图片」批量选取二阶段图片文件</div>
        )}
      </Modal>

      {/* Boss批量技能导入 Modal */}
      <Modal
        title="批量导入Boss技能配置（CSV）"
        open={bossSkillImportOpen}
        onCancel={() => { if (!bossSkillImportLoading) setBossSkillImportOpen(false); }}
        width={820}
        footer={[
          <Button key="tpl" icon={<DownloadOutlined />} onClick={downloadBossSkillTemplate}>下载模板</Button>,
          <Button key="sel" icon={<UploadOutlined />} onClick={() => bossSkillFileInputRef.current?.click()} disabled={bossSkillImportLoading}>选择CSV文件</Button>,
          <Button key="go" type="primary" loading={bossSkillImportLoading}
            disabled={bossSkillImportRows.filter(r => !r._error).length === 0 || !!bossSkillImportDone}
            onClick={handleBossSkillImport}>
            导入 {bossSkillImportRows.filter(r => !r._error).length} 条
          </Button>,
          <Button key="close" onClick={() => setBossSkillImportOpen(false)}>关闭</Button>,
        ]}
      >
        {bossSkillImportLoading && <Progress percent={bossSkillImportProgress} style={{ marginBottom: 12 }} />}
        {bossSkillImportDone && (
          <Alert
            type={bossSkillImportDone.fail === 0 ? 'success' : 'warning'}
            message={`导入完成：成功 ${bossSkillImportDone.ok} 条，失败 ${bossSkillImportDone.fail} 条`}
            style={{ marginBottom: 12 }}
          />
        )}
        {bossSkillImportRows.length > 0 ? (
          <Table
            rowKey="_line"
            size="small"
            dataSource={bossSkillImportRows}
            pagination={{ pageSize: 15 }}
            rowClassName={(r: BossSkillImportRow) => r._error ? 'import-row-error' : ''}
            columns={[
              { title: '行', dataIndex: '_line', width: 45 },
              {
                title: 'Boss编码', dataIndex: 'bossCode', width: 160,
                render: (v: string) => {
                  const found = data.find(b => b.code === v);
                  return found
                    ? <><Tag color="blue">{v}</Tag>{found.name}</>
                    : <Tag color="red">{v} ⚠未找到</Tag>;
                },
              },
              {
                title: '技能（最多4个）', dataIndex: 'skillNames', width: 300,
                render: (names: string[]) => names.map(name => {
                  const found = allSkills.find(s => s.name.trim() === name.trim());
                  return (
                    <Tag key={name} color={found ? 'blue' : 'orange'} style={{ margin: '0 2px 2px 0' }}>
                      {name}{!found && ' ⚠'}
                    </Tag>
                  );
                }),
              },
              {
                title: '状态', width: 130,
                render: (_: any, r: BossSkillImportRow) => r._error
                  ? <span style={{ color: '#ff4d4f', fontSize: 12 }}>{r._error}</span>
                  : <span style={{ color: '#52c41a', fontSize: 12 }}>✓ 正常</span>,
              },
            ]}
          />
        ) : !bossSkillImportDone && (
          <Alert
            type="info"
            message="CSV 格式说明"
            description={
              <div style={{ fontSize: 12 }}>
                <p>列顺序：<code>bossCode, skill1, skill2, skill3, skill4</code></p>
                <p>• <b>bossCode</b>：Boss的编码（如 SHJ_N_001），每行对应一个Boss</p>
                <p>• <b>skill1~skill4</b>：技能名称，需与技能库中的名称完全一致；空列可不填</p>
                <p>• 每行最多4个技能；导入后会覆盖该Boss原有的技能挂载</p>
                <p>• 橙色标签 ⚠ 表示技能名未在技能库中匹配，该技能会被跳过</p>
              </div>
            }
          />
        )}
      </Modal>

      {/* Boss二阶段批量技能导入 Modal */}
      <Modal
        title="批量导入Boss二阶段技能配置（CSV）"
        open={bossP2SkillImportOpen}
        onCancel={() => { if (!bossP2SkillImportLoading) setBossP2SkillImportOpen(false); }}
        width={820}
        footer={[
          <Button key="tpl" icon={<DownloadOutlined />} onClick={downloadBossP2SkillTemplate}>下载模板</Button>,
          <Button key="sel" icon={<UploadOutlined />} onClick={() => bossP2SkillFileInputRef.current?.click()} disabled={bossP2SkillImportLoading}>选择CSV文件</Button>,
          <Button key="go" type="primary" loading={bossP2SkillImportLoading}
            disabled={bossP2SkillImportRows.filter(r => !r._error).length === 0 || !!bossP2SkillImportDone}
            onClick={handleBossP2SkillImport}>
            导入 {bossP2SkillImportRows.filter(r => !r._error).length} 条
          </Button>,
          <Button key="close" onClick={() => setBossP2SkillImportOpen(false)}>关闭</Button>,
        ]}
      >
        {bossP2SkillImportLoading && <Progress percent={bossP2SkillImportProgress} style={{ marginBottom: 12 }} />}
        {bossP2SkillImportDone && (
          <Alert
            type={bossP2SkillImportDone.fail === 0 ? 'success' : 'warning'}
            message={`导入完成：成功 ${bossP2SkillImportDone.ok} 条，失败 ${bossP2SkillImportDone.fail} 条`}
            style={{ marginBottom: 12 }}
          />
        )}
        {bossP2SkillImportRows.length > 0 ? (
          <Table
            rowKey="_line"
            size="small"
            dataSource={bossP2SkillImportRows}
            pagination={{ pageSize: 15 }}
            rowClassName={(r: BossP2SkillImportRow) => r._error ? 'import-row-error' : ''}
            columns={[
              { title: '行', dataIndex: '_line', width: 45 },
              {
                title: 'Boss编码', dataIndex: 'bossCode', width: 160,
                render: (v: string) => {
                  const found = data.find(b => b.code === v);
                  const hasP2 = found && (found.rarity === 'SSR' || found.rarity === 'UR');
                  if (!found) return <Tag color="red">{v} ⚠未找到</Tag>;
                  if (!hasP2) return <><Tag color="orange">{v}</Tag><span style={{ color: '#fa8c16', fontSize: 12 }}>{found.name} ⚠非SSR/UR</span></>;
                  return <><Tag color="purple">{v}</Tag>{found.name}</>;
                },
              },
              {
                title: '二阶段技能（最多4个）', dataIndex: 'skillNames', width: 300,
                render: (names: string[]) => names.map(name => {
                  const found = allSkills.find(s => s.name.trim() === name.trim());
                  return (
                    <Tag key={name} color={found ? 'purple' : 'orange'} style={{ margin: '0 2px 2px 0' }}>
                      {name}{!found && ' ⚠'}
                    </Tag>
                  );
                }),
              },
              {
                title: '状态', width: 130,
                render: (_: any, r: BossP2SkillImportRow) => r._error
                  ? <span style={{ color: '#ff4d4f', fontSize: 12 }}>{r._error}</span>
                  : <span style={{ color: '#52c41a', fontSize: 12 }}>✓ 正常</span>,
              },
            ]}
          />
        ) : !bossP2SkillImportDone && (
          <Alert
            type="info"
            message="CSV 格式说明"
            description={
              <div style={{ fontSize: 12 }}>
                <p>列顺序：<code>bossCode, skill1, skill2, skill3, skill4</code></p>
                <p>• <b>bossCode</b>：Boss的编码（如 SHJ_SSR_001），仅SSR/UR Boss有二阶段</p>
                <p>• <b>skill1~skill4</b>：技能名称，需与技能库中的名称完全一致；空列可不填</p>
                <p>• 每行最多4个技能；导入后会覆盖该Boss二阶段原有的技能挂载</p>
                <p>• 橙色标签 ⚠ 表示技能名未在技能库中匹配，该技能会被跳过</p>
                <p>• 导入前请确保该Boss已通过「二阶段导入」或手动配置了二阶段数据</p>
              </div>
            }
          />
        )}
      </Modal>
    </Card>
  );
};

// ==================== 技能 CSV 导入工具函数 ====================
const SKILL_CSV_HEADERS = ['name','nameEn','attribute','skillType','power','statusEffect','statusChance','statusDuration','criticalChance','criticalDamage','description'];
const SKILL_CSV_TEMPLATE_ROW = ['炎爆冲击','Flame Burst','火','damage','115','','0','1','10','150','以炎火之力猛烈冲击'];

interface SkillImportRow {
  _line: number;
  name: string; nameEn?: string; attribute: string; skillType: string;
  power: number; statusEffect?: string; statusChance: number; statusDuration: number;
  criticalChance: number; criticalDamage: number; description?: string;
  _error?: string;
}

function parseSkillCSV(text: string): SkillImportRow[] {
  // 去除 BOM
  const cleaned = text.replace(/^\uFEFF/, '');
  const lines = cleaned.split(/\r?\n/).filter(l => l.trim());
  const dataLines = lines[0].toLowerCase().startsWith('name') ? lines.slice(1) : lines;
  return dataLines.map((line, idx) => {
    const cols = parseCSVLine(line);
    const [name, nameEn, attribute, skillType, power, statusEffect, statusChance, statusDuration, criticalChance, criticalDamage, description] = cols;
    const errors: string[] = [];
    if (!name) errors.push('缺少技能名称');
    if (!attribute) errors.push('缺少属性');
    const validTypes = ['damage', 'status', 'mixed', 'heal'];
    if (skillType && !validTypes.includes(skillType)) errors.push('技能类型须为 damage/status/mixed/heal');
    const parsedPower = power !== '' && power !== undefined ? Number(power) : 100;
    const parsedStatusDuration = statusDuration !== '' && statusDuration !== undefined ? Number(statusDuration) : 1;
    const parsedCriticalDamage = criticalDamage !== '' && criticalDamage !== undefined ? Number(criticalDamage) : 150;
    return {
      _line: idx + 2,
      name: name?.trim(), nameEn: nameEn?.trim() || undefined,
      attribute: attribute?.trim() || '混沌',
      skillType: skillType?.trim() || 'damage',
      power: isNaN(parsedPower) ? 100 : parsedPower,
      statusEffect: statusEffect?.trim() || undefined,
      statusChance: Number(statusChance) || 0,
      statusDuration: isNaN(parsedStatusDuration) ? 1 : parsedStatusDuration,
      criticalChance: Number(criticalChance) || 0,
      criticalDamage: isNaN(parsedCriticalDamage) ? 150 : parsedCriticalDamage,
      description: description?.trim() || undefined,
      _error: errors.length ? errors.join('；') : undefined,
    };
  }).filter(r => r.name);
}

function downloadSkillTemplate() {
  const csv = [SKILL_CSV_HEADERS.join(','), SKILL_CSV_TEMPLATE_ROW.join(',')].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'skill_import_template.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ==================== 技能 Tab ====================
const SkillsTab: React.FC = () => {
  const [data, setData] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Skill | null>(null);
  const [form] = Form.useForm();
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importRows, setImportRows] = useState<SkillImportRow[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importDone, setImportDone] = useState<{ ok: number; fail: number } | null>(null);
  const skillFileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/skills`);
      const json = await res.json();
      if (json.success) setData(json.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (r: Skill) => { setEditing(r); form.setFieldsValue(r); setModalOpen(true); };

  const handleSubmit = async (values: any) => {
    try {
      const url = editing ? `${BASE_URL}/skills/${editing.id}` : `${BASE_URL}/skills`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(values) });
      const json = await res.json();
      if (json.success) { message.success('保存成功'); setModalOpen(false); load(); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  const handleDelete = async (id: number) => {
    const res = await fetch(`${BASE_URL}/skills/${id}`, { method: 'DELETE' });
    const json = await res.json();
    if (json.success) { message.success('删除成功'); load(); }
    else message.error(json.message);
  };

  const openImportModal = () => {
    setImportRows([]);
    setImportDone(null);
    setImportProgress(0);
    setImportModalOpen(true);
  };

  const handleSkillFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const text = ev.target?.result as string;
      const rows = parseSkillCSV(text);
      setImportRows(rows);
      setImportDone(null);
    };
    reader.readAsText(file, 'utf-8');
    e.target.value = '';
  };

  const handleSkillImport = async () => {
    const validRows = importRows.filter(r => !r._error);
    if (!validRows.length) { message.warning('没有可导入的有效数据'); return; }
    setImportLoading(true);
    setImportProgress(0);
    let ok = 0; let fail = 0; let firstError = '';
    for (let i = 0; i < validRows.length; i++) {
      const { _line, _error, ...body } = validRows[i] as any;
      try {
        const res = await fetch(`${BASE_URL}/skills`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const json = await res.json();
        if (json.success) ok++;
        else { fail++; if (!firstError) firstError = json.message ?? '未知错误'; }
      } catch (e: any) { fail++; if (!firstError) firstError = e.message; }
      setImportProgress(Math.round(((i + 1) / validRows.length) * 100));
    }
    setImportLoading(false);
    setImportDone({ ok, fail });
    if (fail > 0 && firstError) message.error(`导入失败原因：${firstError}`, 8);
    if (ok > 0) load();
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 50 },
    {
      title: '名称', key: 'name', width: 150,
      render: (_: any, r: Skill) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 600 }}>{r.name}</span>
          {r.nameEn && <span style={{ fontSize: 11, color: '#888' }}>{r.nameEn}</span>}
        </Space>
      ),
    },
    { title: '属性', dataIndex: 'attribute', width: 70, render: (v: string) => <Tag>{v}</Tag> },
    {
      title: '类型', dataIndex: 'skillType', width: 80,
      render: (v: string) => {
        const map: Record<string, [string, string]> = {
          damage: ['red', '⚔ 伤害'], status: ['purple', '✨ 状态'],
          mixed: ['orange', '⚔✨ 混合'], heal: ['green', '💚 治疗'],
        };
        const [color, label] = map[v] ?? ['default', v];
        return <Tag color={color}>{label}</Tag>;
      },
    },
    { title: '倍率', dataIndex: 'power', width: 60, render: (v: number, r: Skill) => r.skillType !== 'status' ? `${v}%` : '—' },
    {
      title: '暴击', key: 'critical', width: 110,
      render: (_: any, r: Skill) => r.criticalChance > 0
        ? <Space size={4}><Tag color="gold">{r.criticalChance}%概率</Tag><Tag color="orange">{r.criticalDamage}%伤害</Tag></Space>
        : <span style={{ color: '#999', fontSize: 12 }}>—</span>,
    },
    {
      title: '状态异常', key: 'status', width: 160,
      render: (_: any, r: Skill) => r.statusEffect ? (
        <Space size={4}>
          <Tag color="purple">{r.statusEffect}</Tag>
          <span style={{ fontSize: 11, color: '#888' }}>{r.statusChance}% / {r.statusDuration}回合</span>
        </Space>
      ) : <span style={{ color: '#ccc' }}>—</span>,
    },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    {
      title: '操作', width: 100,
      render: (_: any, r: Skill) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="技能管理"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
          <Button icon={<DownloadOutlined />} onClick={downloadSkillTemplate}>下载模板</Button>
          <Button icon={<UploadOutlined />} onClick={openImportModal}>批量导入</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增技能</Button>
        </Space>
      }
    >
      <input ref={skillFileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleSkillFileChange} />
      <Table rowKey="id" dataSource={data} columns={columns} loading={loading} size="small" pagination={{ pageSize: 30 }} />

      {/* 批量导入 Modal */}
      <Modal
        title="批量导入技能（CSV）"
        open={importModalOpen}
        onCancel={() => { if (!importLoading) setImportModalOpen(false); }}
        width={900}
        footer={[
          <Button key="tpl" icon={<DownloadOutlined />} onClick={downloadSkillTemplate}>下载模板</Button>,
          <Button key="sel" icon={<UploadOutlined />} onClick={() => skillFileInputRef.current?.click()}>选择 CSV 文件</Button>,
          <Button
            key="import" type="primary" loading={importLoading}
            disabled={importRows.filter(r => !r._error).length === 0}
            onClick={handleSkillImport}
          >
            导入 {importRows.filter(r => !r._error).length} 条有效记录
          </Button>,
        ]}
      >
        {importLoading && <Progress percent={importProgress} style={{ marginBottom: 12 }} />}
        {importDone && (
          <Alert
            type={importDone.fail === 0 ? 'success' : 'warning'}
            message={`导入完成：成功 ${importDone.ok} 条，失败 ${importDone.fail} 条`}
            style={{ marginBottom: 12 }}
          />
        )}
        {importRows.length > 0 && (
          <Table
            rowKey="_line"
            size="small"
            dataSource={importRows}
            rowClassName={(r: SkillImportRow) => r._error ? 'import-row-error' : ''}
            pagination={{ pageSize: 20 }}
            columns={[
              { title: '行', dataIndex: '_line', width: 40 },
              { title: '名称', dataIndex: 'name', width: 120 },
              { title: '属性', dataIndex: 'attribute', width: 60, render: (v: string) => <Tag>{v}</Tag> },
              { title: '类型', dataIndex: 'skillType', width: 70 },
              { title: '威力', dataIndex: 'power', width: 60, render: (v: number) => `${v}%` },
              { title: '状态效果', dataIndex: 'statusEffect', width: 100, render: (v: string) => v || '—' },
              { title: '暴击率', dataIndex: 'criticalChance', width: 70, render: (v: number) => v ? `${v}%` : '—' },
              {
                title: '错误', width: 180,
                render: (_: any, r: SkillImportRow) => r._error
                  ? <span style={{ color: '#ff4d4f', fontSize: 12 }}>{r._error}</span>
                  : <span style={{ color: '#52c41a', fontSize: 12 }}>✓ 正常</span>,
              },
            ]}
          />
        )}
        {importRows.length === 0 && !importDone && (
          <Alert
            type="info"
            message="CSV 格式说明"
            description={
              <div style={{ fontSize: 12 }}>
                <p>列顺序：<code>name, nameEn, attribute, skillType, power, statusEffect, statusChance, statusDuration, criticalChance, criticalDamage, description</code></p>
                <p>• <b>skillType</b>：damage / status / mixed / heal</p>
                <p>• <b>attribute</b>：金 / 木 / 水 / 火 / 土 / 雷 / 风 / 毒 / 混沌</p>
                <p>• <b>power</b>：伤害倍率（%ATK），status 类型填 0</p>
                <p>• <b>statusChance</b>：0-100 概率，无状态填 0</p>
              </div>
            }
          />
        )}
      </Modal>

      <Modal title={editing ? '编辑技能' : '新增技能'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={() => form.submit()} okText="保存" width={560}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="name" label="技能名称" rules={[{ required: true }]}><Input placeholder="烈焰冲击" /></Form.Item></Col>
            <Col span={12}><Form.Item name="nameEn" label="英文名"><Input placeholder="Flame Strike" /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="attribute" label="属性" rules={[{ required: true }]}>
                <Select showSearch placeholder="属性">
                  {ATTR_PRESETS.map(a => <Select.Option key={a} value={a}>{a}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="skillType" label="技能类型" initialValue="damage">
                <Select>
                  <Select.Option value="damage">⚔ 伤害</Select.Option>
                  <Select.Option value="heal">💚 治疗</Select.Option>
                  <Select.Option value="status">✨ 状态</Select.Option>
                  <Select.Option value="mixed">⚔✨ 混合</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item shouldUpdate={(prev, cur) => prev.skillType !== cur.skillType} noStyle>
                {({ getFieldValue }) => {
                  const t = getFieldValue('skillType');
                  const isStatus = t === 'status';
                  const label = t === 'heal' ? '治疗量(固定HP)' : '伤害倍率(%ATK)';
                  return (
                    <Form.Item name="power" label={label} initialValue={100}>
                      <InputNumber style={{ width: '100%' }} min={0} max={500} disabled={isStatus} />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="criticalChance" label="暴击概率(%)" initialValue={0} tooltip="0 = 无暴击；1-100 = 触发概率">
                <InputNumber style={{ width: '100%' }} min={0} max={100} addonAfter="%" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="criticalDamage" label="暴击伤害(%)" initialValue={150} tooltip="暴击时造成的伤害倍率，如 150 = 1.5 倍伤害">
                <InputNumber style={{ width: '100%' }} min={100} max={1000} addonAfter="%" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item shouldUpdate={(prev, cur) => prev.skillType !== cur.skillType} noStyle>
            {({ getFieldValue }) => !['damage', 'heal'].includes(getFieldValue('skillType')) && (
              <Row gutter={12}>
                <Col span={10}>
                  <Form.Item name="statusEffect" label="状态异常">
                    <Select placeholder="选择状态">
                      {[['sleep','💤 沉睡'],['poison','☠ 中毒'],['burn','🔥 着火'],['paralysis','⚡ 麻痹'],['confusion','🌀 混乱']].map(([v, l]) => (
                        <Select.Option key={v} value={v}>{l}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item name="statusChance" label="触发概率(%)" initialValue={50}>
                    <InputNumber style={{ width: '100%' }} min={1} max={100} />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item name="statusDuration" label="持续回合" initialValue={2}>
                    <InputNumber style={{ width: '100%' }} min={1} max={10} />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Form.Item>
          <Form.Item name="description" label="描述"><Input.TextArea rows={2} placeholder="技能说明" /></Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

// ==================== 道具 Tab ====================
const ITEM_TYPE_LABEL: Record<string, string> = {
  hp_restore: '🧪 恢复HP', clear_status: '✨ 清除异常', revive: '💊 复活',
  exp_boost: '📖 经验值', atk_boost: '⚔ 攻击增益', def_boost: '🛡 防御增益',
  spd_boost: '💨 闪避增益', escape: '🚪 逃跑',
};
const ITEM_TYPE_COLOR: Record<string, string> = {
  hp_restore: 'green', clear_status: 'purple', revive: 'red',
  exp_boost: 'blue', atk_boost: 'volcano', def_boost: 'geekblue',
  spd_boost: 'cyan', escape: 'default',
};

// ---- 道具CSV导入工具函数 ----
const ITEM_CSV_HEADERS = ['name','nameEn','type','value','valuePercent','duration','description'];
const ITEM_CSV_TEMPLATE_ROW = ['灵芝草','Lingzhi Herb','hp_restore','30','0','0','山中珍稀灵草，恢复30点生命值'];

interface ItemImportRow {
  _line: number;
  name: string; nameEn?: string; type: string;
  value: number; valuePercent: number; duration: number; description?: string;
  _error?: string;
}

function parseItemCSV(text: string): ItemImportRow[] {
  // 去除 BOM
  const cleaned = text.replace(/^\uFEFF/, '');
  const lines = cleaned.split(/\r?\n/).filter(l => l.trim());
  const dataLines = lines[0].toLowerCase().startsWith('name') ? lines.slice(1) : lines;
  const validTypes = ['hp_restore','clear_status','revive','exp_boost','atk_boost','def_boost','spd_boost','escape'];
  return dataLines.map((line, idx) => {
    const cols = parseCSVLine(line);
    const [name, nameEn, type, value, valuePercent, duration, description] = cols;
    const errors: string[] = [];
    if (!name) errors.push('缺少名称');
    if (type && !validTypes.includes(type)) errors.push(`无效道具类型"${type}"，须为：${validTypes.join('/')}`);
    return {
      _line: idx + 2,
      name: name?.trim(), nameEn: nameEn?.trim() || undefined,
      type: type?.trim() || 'hp_restore',
      value: Number(value) || 0,
      valuePercent: Number(valuePercent) || 0,
      duration: Number(duration) || 0,
      description: description?.trim() || undefined,
      _error: errors.length ? errors.join('；') : undefined,
    };
  }).filter(r => r.name);
}

function downloadItemTemplate() {
  const csv = [ITEM_CSV_HEADERS.join(','), ITEM_CSV_TEMPLATE_ROW.join(',')].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'item_import_template.csv'; a.click();
  URL.revokeObjectURL(url);
}

const ItemsTab: React.FC = () => {
  const [data, setData] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Item | null>(null);
  const [form] = Form.useForm();
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importRows, setImportRows] = useState<ItemImportRow[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importDone, setImportDone] = useState<{ ok: number; fail: number } | null>(null);
  const itemFileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/items`);
      const json = await res.json();
      if (json.success) setData(json.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (r: Item) => { setEditing(r); form.setFieldsValue(r); setModalOpen(true); };

  const handleSubmit = async (values: any) => {
    try {
      const url = editing ? `${BASE_URL}/items/${editing.id}` : `${BASE_URL}/items`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(values) });
      const json = await res.json();
      if (json.success) { message.success('保存成功'); setModalOpen(false); load(); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  const handleDelete = async (id: number) => {
    const res = await fetch(`${BASE_URL}/items/${id}`, { method: 'DELETE' });
    const json = await res.json();
    if (json.success) { message.success('删除成功'); load(); }
    else message.error(json.message);
  };

  const openImportModal = () => { setImportRows([]); setImportDone(null); setImportProgress(0); setImportModalOpen(true); };

  const handleItemFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { setImportRows(parseItemCSV(ev.target?.result as string)); setImportDone(null); };
    reader.readAsText(file, 'utf-8'); e.target.value = '';
  };

  const handleItemImport = async () => {
    const validRows = importRows.filter(r => !r._error);
    if (!validRows.length) { message.warning('没有可导入的有效数据'); return; }
    setImportLoading(true); setImportProgress(0);
    let ok = 0; let fail = 0; let firstError = '';
    for (let i = 0; i < validRows.length; i++) {
      const { _line, _error, ...body } = validRows[i] as any;
      try {
        const res = await fetch(`${BASE_URL}/items`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        const json = await res.json();
        if (json.success) ok++;
        else { fail++; if (!firstError) firstError = json.message ?? '未知错误'; }
      } catch (e: any) { fail++; if (!firstError) firstError = e.message; }
      setImportProgress(Math.round(((i + 1) / validRows.length) * 100));
    }
    setImportLoading(false); setImportDone({ ok, fail });
    if (fail > 0 && firstError) message.error(`导入失败原因：${firstError}`, 8);
    if (ok > 0) load();
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 50 },
    {
      title: '名称', key: 'name', width: 150,
      render: (_: any, r: Item) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 600 }}>{r.name}</span>
          {r.nameEn && <span style={{ fontSize: 11, color: '#888' }}>{r.nameEn}</span>}
        </Space>
      ),
    },
    { title: '类型', dataIndex: 'type', width: 130, render: (v: string) => <Tag color={ITEM_TYPE_COLOR[v] ?? 'default'}>{ITEM_TYPE_LABEL[v] ?? v}</Tag> },
    {
      title: '效果', key: 'effect', width: 160,
      render: (_: any, r: Item) => {
        const item = r as any;
        if (r.type === 'hp_restore') {
          const parts: string[] = [];
          if (r.value > 0) parts.push(`+${r.value} HP`);
          if (item.valuePercent > 0) parts.push(`+${item.valuePercent}% HP`);
          return parts.length ? <Tag color="green">{parts.join(' + ')}</Tag> : <span style={{ color: '#aaa' }}>0</span>;
        }
        if (r.type === 'revive') return <Tag color="red">复活 {item.valuePercent > 0 ? `+${item.valuePercent}% HP` : '满血'}</Tag>;
        if (r.type === 'exp_boost') return <Tag color="blue">+{r.value} EXP</Tag>;
        if (r.type === 'atk_boost') return <Tag color="volcano">+{r.value} ATK / {item.duration}回合</Tag>;
        if (r.type === 'def_boost') return <Tag color="geekblue">+{r.value} DEF / {item.duration}回合</Tag>;
        if (r.type === 'spd_boost') return <Tag color="cyan">闪避 35% / {item.duration}回合</Tag>;
        if (r.type === 'escape') return <Tag>战斗逃跑</Tag>;
        if (r.type === 'clear_status') return <Tag color="purple">清除异常</Tag>;
        return <span style={{ color: '#aaa' }}>—</span>;
      },
    },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    {
      title: '操作', width: 100,
      render: (_: any, r: Item) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="道具管理"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
          <Button icon={<DownloadOutlined />} onClick={downloadItemTemplate}>下载模板</Button>
          <Button icon={<UploadOutlined />} onClick={openImportModal}>批量导入</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增道具</Button>
        </Space>
      }
    >
      <input ref={itemFileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleItemFileChange} />
      <Table rowKey="id" dataSource={data} columns={columns} loading={loading} size="small" pagination={{ pageSize: 30 }} />

      {/* 批量导入 Modal */}
      <Modal
        title="批量导入道具（CSV）"
        open={importModalOpen}
        onCancel={() => { if (!importLoading) setImportModalOpen(false); }}
        width={860}
        footer={[
          <Button key="tpl" icon={<DownloadOutlined />} onClick={downloadItemTemplate}>下载模板</Button>,
          <Button key="sel" icon={<UploadOutlined />} onClick={() => itemFileInputRef.current?.click()}>选择 CSV 文件</Button>,
          <Button key="go" type="primary" loading={importLoading} disabled={importRows.filter(r => !r._error).length === 0} onClick={handleItemImport}>
            导入 {importRows.filter(r => !r._error).length} 条
          </Button>,
        ]}
      >
        {importLoading && <Progress percent={importProgress} style={{ marginBottom: 12 }} />}
        {importDone && <Alert type={importDone.fail === 0 ? 'success' : 'warning'} message={`导入完成：成功 ${importDone.ok} 条，失败 ${importDone.fail} 条`} style={{ marginBottom: 12 }} />}
        {importRows.length > 0 ? (
          <Table rowKey="_line" size="small" dataSource={importRows} rowClassName={(r: ItemImportRow) => r._error ? 'import-row-error' : ''} pagination={{ pageSize: 20 }}
            columns={[
              { title: '行', dataIndex: '_line', width: 40 },
              { title: '名称', dataIndex: 'name', width: 130 },
              { title: '类型', dataIndex: 'type', width: 110, render: (v: string) => <Tag color={ITEM_TYPE_COLOR[v] ?? 'default'}>{ITEM_TYPE_LABEL[v] ?? v}</Tag> },
              { title: 'value', dataIndex: 'value', width: 60 },
              { title: 'valuePercent', dataIndex: 'valuePercent', width: 80 },
              { title: 'duration', dataIndex: 'duration', width: 65 },
              { title: '状态', width: 160, render: (_: any, r: ItemImportRow) => r._error ? <span style={{ color: '#ff4d4f', fontSize: 12 }}>{r._error}</span> : <span style={{ color: '#52c41a', fontSize: 12 }}>✓ 正常</span> },
            ]}
          />
        ) : !importDone && (
          <Alert type="info" message="CSV 格式说明"
            description={
              <div style={{ fontSize: 12 }}>
                <p>列：<code>name, nameEn, type, value, valuePercent, duration, description</code></p>
                <p>• <b>type</b>：hp_restore / clear_status / revive / exp_boost / atk_boost / def_boost / spd_boost / escape</p>
                <p>• <b>value</b>：hp_restore=固定HP | exp_boost=经验值 | atk/def_boost=提升数值</p>
                <p>• <b>valuePercent</b>：hp_restore=百分比HP | revive=复活后HP百分比</p>
                <p>• <b>duration</b>：atk/def/spd_boost 的持续回合数</p>
              </div>
            }
          />
        )}
      </Modal>

      <Modal title={editing ? '编辑道具' : '新增道具'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={() => form.submit()} okText="保存" width={520}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="name" label="道具名称" rules={[{ required: true }]}><Input placeholder="灵芝草" /></Form.Item></Col>
            <Col span={12}><Form.Item name="nameEn" label="英文名"><Input placeholder="Lingzhi Herb" /></Form.Item></Col>
          </Row>
          <Form.Item name="type" label="道具类型" initialValue="hp_restore" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="hp_restore">🧪 恢复HP（战内/战外）</Select.Option>
              <Select.Option value="clear_status">✨ 清除异常状态（战内）</Select.Option>
              <Select.Option value="revive">💊 复活英雄（战外）</Select.Option>
              <Select.Option value="exp_boost">📖 经验值增益（战外）</Select.Option>
              <Select.Option value="atk_boost">⚔ 临时攻击增益（战内）</Select.Option>
              <Select.Option value="def_boost">🛡 临时防御增益（战内）</Select.Option>
              <Select.Option value="spd_boost">💨 临时闪避增益（战内）</Select.Option>
              <Select.Option value="escape">🚪 逃跑（战内）</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item shouldUpdate={(prev, cur) => prev.type !== cur.type} noStyle>
            {({ getFieldValue }) => {
              const t = getFieldValue('type');
              return (
                <Row gutter={12}>
                  {(t === 'hp_restore' || t === 'atk_boost' || t === 'def_boost' || t === 'exp_boost') && (
                    <Col span={8}>
                      <Form.Item name="value" label={t === 'exp_boost' ? '经验值' : t === 'hp_restore' ? '固定HP' : '提升数值'} initialValue={0}>
                        <InputNumber style={{ width: '100%' }} min={0} />
                      </Form.Item>
                    </Col>
                  )}
                  {(t === 'hp_restore' || t === 'revive') && (
                    <Col span={8}>
                      <Form.Item name="valuePercent" label={t === 'revive' ? '复活HP%' : '百分比HP%'} initialValue={0}>
                        <InputNumber style={{ width: '100%' }} min={0} max={100} addonAfter="%" />
                      </Form.Item>
                    </Col>
                  )}
                  {(t === 'atk_boost' || t === 'def_boost' || t === 'spd_boost') && (
                    <Col span={8}>
                      <Form.Item name="duration" label="持续回合" initialValue={3}>
                        <InputNumber style={{ width: '100%' }} min={1} max={10} addonAfter="回合" />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              );
            }}
          </Form.Item>
          <Form.Item name="description" label="描述"><Input.TextArea rows={2} placeholder="道具说明" /></Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

// ==================== 卡牌 Tab ====================
const CardsTab: React.FC = () => {
  const [data, setData] = useState<GameCard[]>([]);
  const [bosses, setBosses] = useState<Boss[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<GameCard | null>(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cardsRes, bossRes] = await Promise.all([
        fetch(`${BASE_URL}/cards`).then(r => r.json()),
        fetch(`${BASE_URL}/bosses`).then(r => r.json()),
      ]);
      if (cardsRes.success) setData(cardsRes.data);
      if (bossRes.success) setBosses(bossRes.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (r: GameCard) => { setEditing(r); form.setFieldsValue({ cardCode: r.cardCode, bossId: r.bossId, isActive: r.isActive }); setModalOpen(true); };

  const handleSubmit = async (values: any) => {
    try {
      const url = editing ? `${BASE_URL}/cards/${editing.id}` : `${BASE_URL}/cards`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(values) });
      const json = await res.json();
      if (json.success) { message.success('保存成功'); setModalOpen(false); load(); }
      else message.error(json.message);
    } catch (e: any) { message.error(e.message); }
  };

  const handleDelete = async (id: number) => {
    const res = await fetch(`${BASE_URL}/cards/${id}`, { method: 'DELETE' });
    const json = await res.json();
    if (json.success) { message.success('删除成功'); load(); }
    else message.error(json.message);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 50 },
    { title: '卡牌编码', dataIndex: 'cardCode', width: 150 },
    {
      title: '对应Boss', key: 'boss',
      render: (_: any, r: GameCard) => r.boss
        ? <Space><Tag color={RARITY_COLOR[r.boss.rarity]}>{r.boss.rarity}</Tag>{r.boss.name}</Space>
        : '-',
    },
    {
      title: '状态', dataIndex: 'isActive', width: 70,
      render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '创建时间', dataIndex: 'created', width: 160,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
    {
      title: '操作', width: 100,
      render: (_: any, r: GameCard) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="卡牌管理"
      extra={<Space><Button icon={<ReloadOutlined />} onClick={load}>刷新</Button><Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增卡牌</Button></Space>}
    >
      <Table rowKey="id" dataSource={data} columns={columns} loading={loading} size="small" pagination={{ pageSize: 30 }} />
      <Modal title={editing ? '编辑卡牌' : '新增卡牌'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={() => form.submit()} okText="保存">
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="cardCode" label="卡牌编码（QR内容）" rules={[{ required: true }]}>
            <Input placeholder="CARD-001" />
          </Form.Item>
          <Form.Item name="bossId" label="关联Boss" rules={[{ required: true }]}>
            <Select placeholder="选择Boss">
              {bosses.map(b => (
                <Select.Option key={b.id} value={b.id}>
                  <Space><Tag color={RARITY_COLOR[b.rarity]}>{b.rarity}</Tag>{b.name}</Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="isActive" label="状态" initialValue={true}>
            <Select><Select.Option value={true}>启用</Select.Option><Select.Option value={false}>禁用</Select.Option></Select>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

// ==================== 玩家 Tab ====================
const PlayersTab: React.FC = () => {
  const [data, setData] = useState<Player[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/players`);
      const json = await res.json();
      if (json.success) setData(json.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const total = data.length;
  const totalBattles = data.reduce((s, p) => s + (p.hero ? p.hero.battlesWon + p.hero.battlesLost : 0), 0);
  const avgLevel = total > 0 ? (data.reduce((s, p) => s + (p.hero?.level ?? 0), 0) / total).toFixed(1) : '0';

  const columns = [
    { title: 'Hash', dataIndex: 'hash', width: 100, render: (v: string) => <code>{v}</code> },
    { title: '昵称', dataIndex: 'nickname', width: 100 },
    {
      title: '当前英雄', key: 'hero', width: 100,
      render: (_: any, r: Player) => r.hero
        ? <span>{r.hero.heroType?.name ?? '-'}{r.hero.isDead && <Tag color="red" style={{ marginLeft: 4 }}>阵亡</Tag>}</span>
        : <span style={{ color: '#999' }}>无</span>,
    },
    { title: 'Lv', key: 'level', width: 50, render: (_: any, r: Player) => r.hero?.level ?? '-' },
    { title: 'HP', key: 'hp', width: 60, render: (_: any, r: Player) => r.hero?.hp ?? '-' },
    { title: 'ATK', key: 'atk', width: 60, render: (_: any, r: Player) => r.hero?.atk ?? '-' },
    { title: 'DEF', key: 'def', width: 60, render: (_: any, r: Player) => r.hero?.def ?? '-' },
    {
      title: '胜/败', key: 'battles', width: 80,
      render: (_: any, r: Player) => r.hero
        ? <span><span style={{ color: '#52c41a' }}>{r.hero.battlesWon}</span> / <span style={{ color: '#ff4d4f' }}>{r.hero.battlesLost}</span></span>
        : '-',
    },
    {
      title: '创建时间', dataIndex: 'created', width: 160,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
  ];

  return (
    <Card
      title="玩家列表"
      extra={<Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>}
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col><Statistic title="总玩家数" value={total} /></Col>
        <Col><Statistic title="总战斗次数" value={totalBattles} /></Col>
        <Col><Statistic title="平均等级" value={avgLevel} /></Col>
      </Row>
      <Table rowKey="id" dataSource={data} columns={columns} loading={loading} size="small" pagination={{ pageSize: 30 }} />
    </Card>
  );
};

// ==================== 主组件 ====================
const UrbanRpgAdmin: React.FC = () => {
  return (
    <div className="urban-rpg-admin">
      <Tabs defaultActiveKey="heroes">
        <TabPane tab="英雄类型" key="heroes"><HeroTypesTab /></TabPane>
        <TabPane tab="Boss管理" key="bosses"><BossTab /></TabPane>
        <TabPane tab="技能管理" key="skills"><SkillsTab /></TabPane>
        <TabPane tab="道具管理" key="items"><ItemsTab /></TabPane>
        <TabPane tab="卡牌管理" key="cards"><CardsTab /></TabPane>
        <TabPane tab="玩家列表" key="players"><PlayersTab /></TabPane>
      </Tabs>
    </div>
  );
};

export default UrbanRpgAdmin;
